import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import SafeAnimatePresence from '../../components/ui/SafeAnimatePresence';
import { FiRefreshCw, FiSearch, FiFilter, FiChevronDown, FiChevronUp, FiActivity, FiZap, FiX, FiCheckCircle, FiCpu, FiAlertTriangle, FiMessageSquare, FiCalendar, FiShield, FiBarChart2, FiDollarSign, FiSend, FiChevronRight, FiMinus, FiFileText, FiUser, FiTrendingUp, FiUsers, FiTarget, FiTerminal, FiPlusCircle, FiPlay, FiGitBranch, FiCheckSquare, FiRepeat, FiMail, FiClock, FiCheck, FiArrowRight, FiBell, FiFlag, } from 'react-icons/fi';
import '../../styles/RenewalCenter.css';
type PolicyStatus = 'Active' | 'Renewed' | 'Cancelled' | 'Archived';
type AiRecommendation = 'accept' | 'shop' | 'contact';
type AiRisk = 'low' | 'medium' | 'high';
type PipelineStage = 'not_started' | 'outreach' | 'quoting' | 'negotiation' | 'pending_bind' | 'bound' | 'lost';
type AutomationStatus = 'none' | 'monitoring' | 'drip' | 'quote_request' | 'follow_up' | 'full_auto';
type CommandStatus = 'pending' | 'active' | 'completed' | 'failed';
type CommandType = 'shop_quotes' | 'draft_outreach' | 'schedule_followup' | 'bind_policy' | 'request_audit' | 'custom';
type CommandPriority = 'high' | 'medium' | 'low';
interface AiReasoning {
    summary: string;
    factors: string[];
    recommendation: AiRecommendation;
    retentionScore: number;
    alternativeCarriers?: string[];
}
interface CoverageDetail {
    coverage: string;
    limit1: string;
    limit2?: string;
    deductible?: string;
    premium?: string;
}
interface PolicySnapshot {
    period: string;
    status: string;
    lineOfBusiness: string;
    carrier: string;
    mga: string;
    premium: number;
    nonPremium: number;
    coverages: CoverageDetail[];
    vehicles?: {
        vin: string;
        make: string;
        model: string;
        year: string;
        value: string;
    }[];
    drivers?: {
        name: string;
        dob: string;
        license: string;
    }[];
    notes?: string;
}
interface AutomationInfo {
    status: AutomationStatus;
    actionsCount?: number;
    lastAction?: string;
}
interface CRMOpportunity {
    id: string;
    stage: string;
    value: number;
    label: string;
}
interface PolicyCommand {
    id: string;
    title: string;
    status: CommandStatus;
}
interface RenewalRow {
    id: string;
    expDate: string;
    number: string;
    status: PolicyStatus;
    insured: string;
    lineOfBusiness: string;
    carrier: string;
    mga: string;
    premium: number;
    renewalPremium: number;
    renewalPolicy: string | null;
    renewalQuote: string | null;
    pctChange: number;
    dollarChange: number;
    parentCarrier: string;
    aiRisk: AiRisk;
    aiReasoning: AiReasoning;
    currentSnapshot?: PolicySnapshot;
    renewalSnapshot?: PolicySnapshot;
    pipelineStage: PipelineStage;
    automation: AutomationInfo;
    opportunity?: CRMOpportunity;
    commands: PolicyCommand[];
}
type RetentionOutcome = 'cancelled' | 'expired' | 'non_renewed';
type RetentionLostReason = 'rewritten_away' | 'carrier_non_renewed' | 'client_cancelled' | 'rate_increase' | 'unknown';
interface RetentionRow {
    id: string;
    expiredDate: string;
    number: string;
    insured: string;
    lineOfBusiness: string;
    carrier: string;
    mga: string;
    premium: number;
    outcome: RetentionOutcome;
    aiShouldRetain: boolean;
    aiConfidence: number;
    aiAnalysis: string;
    aiFactors: string[];
    evidence: {
        sameLoBDifferentCarrier?: {
            carrier: string;
            policyNumber: string;
            effectiveDate: string;
            estimatedPremium?: number;
        };
    };
    lostReason: RetentionLostReason;
}
interface ChatMsg {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    time: string;
}
type NotifType = 'renewal_received' | 'risk_change' | 'command_completed' | 'price_update' | 'automation_triggered' | 'expiring_today';
interface Notification {
    id: string;
    type: NotifType;
    policyId: string;
    insured: string;
    message: string;
    timestamp: Date;
    read: boolean;
    urgent?: boolean;
}
interface LiveEvent {
    type: NotifType;
    policyId: string;
    insured: string;
    message: string;
    urgent?: boolean;
}
const LIVE_EVENTS: LiveEvent[] = [
    { type: 'expiring_today', policyId: '1', insured: 'Marquiss, Ryan', message: 'Policy SSH1001304-00 expires today — no renewal on file.', urgent: true },
    { type: 'renewal_received', policyId: '2', insured: 'Quezada, Jose', message: 'Renewal policy 9300156320 received from GEICO at $1,325/yr (+17%).', urgent: false },
    { type: 'automation_triggered', policyId: '4', insured: 'TRUECRAFT DRYWALL & A', message: 'Automation: payroll audit request sent to Continental Casualty via BTIS MGA.', urgent: false },
    { type: 'risk_change', policyId: '8', insured: 'Acoustic Life Incorporated', message: 'AI risk level upgraded to HIGH — no contact in 47 days, policy expires in 11d.', urgent: true },
    { type: 'command_completed', policyId: '6', insured: 'Strebe, Thomas', message: 'Command complete: 3 personal auto quotes pulled (Progressive, Travelers, Hippo).', urgent: false },
    { type: 'price_update', policyId: '3', insured: 'Nicely, Edna', message: 'Renewal quote updated — Universal P&C revised premium to $944.87 (+9.2%).', urgent: false },
    { type: 'renewal_received', policyId: '9', insured: 'Dd Fuel & Market', message: 'AmTrust WC renewal TWP4801199 bound at $2,808/yr (+9%). No action needed.', urgent: false },
];
interface MarketCarrier {
    carrier: string;
    tier: 'primary' | 'secondary';
    note: string;
}
const MARKET_APPETITE: Record<string, MarketCarrier[]> = {
    'Homeowners': [
        { carrier: 'Citizens Property Ins.', tier: 'primary', note: 'Coastal-tier, strong FL/TX placement' },
        { carrier: 'Universal P&C', tier: 'primary', note: 'Competitive in TX/FL, quick bind' },
        { carrier: 'Openly', tier: 'primary', note: 'HO3/HO5 specialty, high-value homes' },
        { carrier: 'HCI Group', tier: 'secondary', note: 'Available via Savvi MGA' },
        { carrier: 'Slide Insurance', tier: 'secondary', note: 'Coastal wind specialty' },
    ],
    'Personal Auto': [
        { carrier: 'Progressive', tier: 'primary', note: 'Top TX personal auto appetite' },
        { carrier: 'Travelers', tier: 'primary', note: 'Preferred tier, strong credits' },
        { carrier: 'Hippo Auto', tier: 'primary', note: 'Bundle with HO for discount' },
        { carrier: 'Safeco', tier: 'secondary', note: 'Bundle discount available' },
        { carrier: 'Bristol West', tier: 'secondary', note: 'Non-standard, prior incidents OK' },
    ],
    "Worker's Compensation": [
        { carrier: 'Employers', tier: 'primary', note: 'Best-in-class for small biz WC' },
        { carrier: 'ICW Group', tier: 'primary', note: 'Strong safety program credits' },
        { carrier: 'Zurich WC', tier: 'primary', note: 'Available via BTIS MGA' },
        { carrier: 'Zenith Ins', tier: 'secondary', note: 'Construction & specialty classes' },
        { carrier: 'Compwest', tier: 'secondary', note: 'CA/NV focused' },
    ],
    'Commercial Auto': [
        { carrier: 'Progressive Commercial', tier: 'primary', note: 'Broadest commercial auto appetite' },
        { carrier: 'Nationwide Commercial', tier: 'primary', note: 'Fleet discount available' },
        { carrier: 'Employers', tier: 'primary', note: 'Bundle with WC for discount' },
        { carrier: 'State Auto', tier: 'secondary', note: 'Smaller fleets preferred' },
        { carrier: 'Markel Commercial', tier: 'secondary', note: 'Specialty vehicles OK' },
    ],
};
interface UpsellRule {
    missingLob: string;
    triggerLob: string[];
    blockedBy: string[];
    reason: string;
    estimatedPremium: string;
}
const UPSELL_RULES: UpsellRule[] = [
    {
        missingLob: 'Homeowners',
        triggerLob: ['Personal Auto'],
        blockedBy: ['Homeowners'],
        reason: 'Insured has auto but no homeowners on file — strong bundle opportunity.',
        estimatedPremium: '$900–$1,400/yr',
    },
    {
        missingLob: 'Personal Auto',
        triggerLob: ['Homeowners'],
        blockedBy: ['Personal Auto'],
        reason: 'Insured has homeowners but no personal auto — bundle discount available.',
        estimatedPremium: '$800–$1,200/yr',
    },
    {
        missingLob: 'Umbrella',
        triggerLob: ['Homeowners', 'Personal Auto'],
        blockedBy: ['Umbrella'],
        reason: 'HO + Auto bundle qualifies for umbrella — increases total account value.',
        estimatedPremium: '$300–$600/yr',
    },
    {
        missingLob: 'General Liability',
        triggerLob: ["Worker's Compensation"],
        blockedBy: ['General Liability'],
        reason: 'WC present without GL leaves commercial exposure uncovered.',
        estimatedPremium: '$700–$1,500/yr',
    },
    {
        missingLob: 'Commercial Auto',
        triggerLob: ["Worker's Compensation", 'General Liability'],
        blockedBy: ['Commercial Auto'],
        reason: 'Commercial account has WC/GL but no commercial auto — coverage gap risk.',
        estimatedPremium: '$1,000–$2,500/yr',
    },
    {
        missingLob: 'Flood',
        triggerLob: ['Homeowners'],
        blockedBy: ['Flood'],
        reason: 'Homeowners policy does not cover flood — FEMA zone exposure likely.',
        estimatedPremium: '$400–$900/yr',
    },
];
const MOCK_RENEWALS: RenewalRow[] = [
    {
        id: '1', expDate: '2026-05-20', number: 'SSH1001304-00', status: 'Active',
        insured: 'Marquiss, Ryan', lineOfBusiness: 'Homeowners',
        carrier: 'Lyndon Southern Insurance Company', mga: 'Savvi Insurance Group',
        premium: 1044, renewalPremium: 0, renewalPolicy: null, renewalQuote: null,
        pctChange: 0, dollarChange: 0, parentCarrier: 'Lyndon Southern Insurance Company',
        aiRisk: 'high',
        aiReasoning: {
            summary: 'Policy expires today with no renewal received. Immediate outreach required to prevent a coverage gap.',
            factors: [
                'No renewal quote or policy found in the system as of today',
                'Carrier has non-renewed 14% of coastal-tier homeowners in this zip this cycle',
                'Client has been with the agency 4+ years — strong retention opportunity',
                'Three viable alternative carriers available via Savvi Insurance Group',
            ],
            recommendation: 'contact', retentionScore: 62,
            alternativeCarriers: ['Citizens Property Ins.', 'Universal P&C', 'Openly'],
        },
        pipelineStage: 'outreach',
        automation: { status: 'follow_up', actionsCount: 3, lastAction: 'Email sent 2h ago' },
        commands: [{ id: 'cmd-1', title: 'Draft coverage gap notice for insured', status: 'active' }],
    },
    {
        id: '2', expDate: '2026-05-21', number: '9300156320', status: 'Active',
        insured: 'Quezada, Jose', lineOfBusiness: 'Commercial Auto',
        carrier: 'GEICO County Mutual Insurance Company', mga: 'N/A',
        premium: 1136, renewalPremium: 1325, renewalPolicy: '9300156320', renewalQuote: '9300156320',
        pctChange: 17, dollarChange: 189, parentCarrier: 'GEICO County Mutual Insurance Company',
        aiRisk: 'medium',
        aiReasoning: {
            summary: '+17% increase driven by GEICO\'s statewide rate filing in TX and an added vehicle on the policy.',
            factors: [
                'GEICO filed a 12.3% commercial auto rate increase in TX (effective Feb 2026)',
                'New vehicle added: 2023 Ford F-150 — adds ~$45 to annual premium',
                'Commercial auto frequency losses up 6.8% in DFW market this cycle',
                'Client credit tier unchanged — no adverse impact from that factor',
            ],
            recommendation: 'shop', retentionScore: 74,
            alternativeCarriers: ['Progressive Commercial', 'Nationwide Commercial', 'Employers'],
        },
        currentSnapshot: {
            period: '05/21/2025 – 05/21/2026', status: 'Active', lineOfBusiness: 'New Business',
            carrier: 'GEICO County Mutual Insurance Company', mga: 'N/A',
            premium: 1136, nonPremium: 5,
            coverages: [{ coverage: 'Combined Single Limit', limit1: '$500,000', premium: '$1,330' }],
            vehicles: [{ vin: '1FTRE14233HA78658', make: 'FORD', model: 'E-150', year: '2003', value: 'N/A' }],
            drivers: [{ name: 'Jose Quezada', dob: '09/07/1962', license: '17361773 TX' }],
        },
        renewalSnapshot: {
            period: '05/21/2026 – 05/21/2027', status: 'Renewing', lineOfBusiness: 'Renewal',
            carrier: 'GEICO County Mutual Insurance Company', mga: 'N/A',
            premium: 1325, nonPremium: 5,
            coverages: [
                { coverage: 'Combined Single Limit', limit1: '$500,000', premium: '$1,330' },
                { coverage: 'Motor Vehicle Crime Fee', limit1: '—', premium: '—' },
                { coverage: 'Additional Insured', limit1: '—', premium: '—' },
            ],
            vehicles: [{ vin: '1FTRE14233HA78658', make: 'FORD', model: 'E-150', year: '2003', value: 'N/A' }],
            drivers: [{ name: 'Jose Quezada', dob: '09/07/1962', license: '17361773 TX' }],
            notes: 'Custom coverages added on renewal — verify with insured.',
        },
        pipelineStage: 'negotiation',
        automation: { status: 'quote_request', actionsCount: 2, lastAction: 'Quote pulled 1d ago' },
        opportunity: { id: 'opp-1', stage: 'Proposal Sent', value: 1325, label: 'Renewal' },
        commands: [{ id: 'cmd-2', title: 'Shop 3 alternative commercial carriers', status: 'active' }],
    },
    {
        id: '3', expDate: '2026-05-22', number: '7501-2500-2503', status: 'Cancelled',
        insured: 'Nicely, Edna', lineOfBusiness: 'Homeowners',
        carrier: 'Universal Prop & Cas', mga: 'N/A',
        premium: 865.14, renewalPremium: 944.87, renewalPolicy: '7501-2500-2503', renewalQuote: '7501-2500-2503',
        pctChange: 9, dollarChange: 79.73, parentCarrier: 'Universal Prop & Cas',
        aiRisk: 'medium',
        aiReasoning: {
            summary: 'Policy was mid-term cancelled. Renewal quote exists but requires client re-qualification.',
            factors: [
                'Policy cancelled mid-term — renewal eligibility may be restricted by carrier',
                'Universal P&C raised homeowners rates 9.2% across FL and TX book',
                'No reported claims on this policy — favorable re-qualification profile',
                'Reinstatement window closes in 14 days; quick outreach recommended',
            ],
            recommendation: 'contact', retentionScore: 55,
        },
        pipelineStage: 'outreach',
        automation: { status: 'drip', actionsCount: 1, lastAction: 'Welcome email sent' },
        opportunity: { id: 'opp-2', stage: 'New Lead', value: 0, label: 'Win-back' },
        commands: [],
    },
    {
        id: '4', expDate: '2026-05-30', number: '7039313569', status: 'Active',
        insured: 'TRUECRAFT DRYWALL & A', lineOfBusiness: "Worker's Compensation",
        carrier: 'Continental Casualty Company', mga: 'BTIS',
        premium: 854, renewalPremium: 0, renewalPolicy: null, renewalQuote: null,
        pctChange: 0, dollarChange: 0, parentCarrier: 'CNA Ins Grp/Iroquois',
        aiRisk: 'high',
        aiReasoning: {
            summary: 'WC policy expiring in 10 days with no renewal in system. Missing payroll audit may be blocking carrier renewal.',
            factors: [
                'Continental Casualty typically issues WC renewals 45 days prior — overdue',
                'Payroll audit for 2025 policy year not yet completed per carrier records',
                'BTIS MGA has a 72-hr turn on WC apps — alternative quote achievable before expiration',
                'No claims in the past 24 months — excellent experience modifier outlook',
            ],
            recommendation: 'contact', retentionScore: 68,
            alternativeCarriers: ['Employers', 'Zurich WC', 'ICW Group'],
        },
        pipelineStage: 'quoting',
        automation: { status: 'monitoring', lastAction: 'Rate alert triggered' },
        commands: [{ id: 'cmd-3', title: 'Complete payroll audit request to carrier', status: 'active' }],
    },
    {
        id: '5', expDate: '2026-05-31', number: 'OF3361557', status: 'Active',
        insured: 'Strebe, Thomas', lineOfBusiness: 'Homeowners',
        carrier: 'Safeco Ins Co of IL', mga: 'N/A',
        premium: 1243, renewalPremium: 1256, renewalPolicy: 'OF3361557', renewalQuote: 'OF3361557',
        pctChange: 1, dollarChange: 13, parentCarrier: 'Safeco Ins Co of IL',
        aiRisk: 'low',
        aiReasoning: {
            summary: 'Minimal 1% increase — well within acceptable range. Renewal is in good standing.',
            factors: [
                'Safeco applied standard inflationary adjustment of 1.05% to dwelling coverage',
                'No claims in policy period — no experience surcharge',
                'Credit score improvement reflected in base rate — partially offsets adjustment',
                'Dwelling replacement cost estimate updated to current index',
            ],
            recommendation: 'accept', retentionScore: 91,
        },
        pipelineStage: 'pending_bind',
        automation: { status: 'monitoring', lastAction: 'Renewal docs received' },
        commands: [],
    },
    {
        id: '6', expDate: '2026-05-31', number: 'F4034641', status: 'Active',
        insured: 'Strebe, Thomas', lineOfBusiness: 'Personal Auto',
        carrier: 'American Economy Ins Co (Safeco)', mga: 'N/A',
        premium: 981, renewalPremium: 1102, renewalPolicy: 'F4034641', renewalQuote: 'F4034641',
        pctChange: 12, dollarChange: 121, parentCarrier: 'American Economy Ins Co (Safeco)',
        aiRisk: 'medium',
        aiReasoning: {
            summary: '+12% driven by Texas personal auto rate hikes and increased repair costs post-hail season.',
            factors: [
                'Safeco filed 11.8% personal auto rate increase in TX effective Jan 2026',
                'Bodily injury frequency trends in Dallas metro up 8.4% year-over-year',
                'No at-fault claims for this insured — clean record maintained',
                'Bundle discount with homeowners policy (OF3361557) already applied',
            ],
            recommendation: 'shop', retentionScore: 78,
            alternativeCarriers: ['Progressive', 'Travelers', 'Hippo Auto'],
        },
        pipelineStage: 'quoting',
        automation: { status: 'quote_request', actionsCount: 1, lastAction: 'Progressive quote requested' },
        opportunity: { id: 'opp-3', stage: 'Contacted', value: 981, label: 'Cross-sell' },
        commands: [{ id: 'cmd-4', title: 'Pull 3 competitive personal auto quotes', status: 'active' }],
    },
    {
        id: '7', expDate: '2026-06-01', number: 'EIGS27345302', status: 'Renewed',
        insured: 'StartUpNV', lineOfBusiness: "Worker's Compensation",
        carrier: 'Employers preferred insurance company', mga: 'Talage Bo',
        premium: 1472, renewalPremium: 1626, renewalPolicy: 'EIGS97668901', renewalQuote: 'EIGS97668901',
        pctChange: 10, dollarChange: 154, parentCarrier: 'Employers Holding Group',
        aiRisk: 'low',
        aiReasoning: {
            summary: 'Policy successfully renewed. 10% increase is in line with Nevada WC industry trend for this NAICS class.',
            factors: [
                'NV WC loss costs increased 9.7% for NAICS 5411 (Professional Services) this filing year',
                'Client payroll grew by 8% — increased exposure base drives ~3% of the total change',
                'Experience modifier held at 0.94 — favorable claims management',
                'Renewal policy bound and confirmed — no action needed',
            ],
            recommendation: 'accept', retentionScore: 95,
        },
        pipelineStage: 'bound',
        automation: { status: 'none' },
        opportunity: { id: 'opp-4', stage: 'Proposal Sent', value: 1626, label: 'Upsell' },
        commands: [{ id: 'cmd-5', title: 'Bind confirmation sent to insured', status: 'completed' }],
    },
    {
        id: '8', expDate: '2026-06-01', number: 'EIGS98263000', status: 'Active',
        insured: 'Acoustic Life Incorporated', lineOfBusiness: "Worker's Compensation",
        carrier: 'Employers Compensation Ins Co', mga: 'Talage Bo',
        premium: 758, renewalPremium: 0, renewalPolicy: null, renewalQuote: null,
        pctChange: 0, dollarChange: 0, parentCarrier: 'Employers Holding Group',
        aiRisk: 'high',
        aiReasoning: {
            summary: 'No renewal received — policy expiring in 11 days. Insured may have changed carriers or gone direct.',
            factors: [
                'Employers typically sends renewal 30 days prior — no communication received',
                'Talage Bo market access check shows policy in "pending review" status',
                'Insured last contacted via email 47 days ago — engagement gap',
                'Similar accounts in this class renewing at ~4–6% increase this cycle',
            ],
            recommendation: 'contact', retentionScore: 48,
            alternativeCarriers: ['ICW Group', 'Zenith Ins', 'EMPLOYERS (direct)'],
        },
        pipelineStage: 'outreach',
        automation: { status: 'drip', actionsCount: 4, lastAction: 'Follow-up #3 sent 3d ago' },
        commands: [{ id: 'cmd-6', title: 'Escalate to producer — no response in 47d', status: 'pending' }],
    },
    {
        id: '9', expDate: '2026-06-01', number: 'QWC1461314', status: 'Renewed',
        insured: 'Dd Fuel & Market', lineOfBusiness: "Worker's Compensation",
        carrier: 'Amtrust Financial', mga: 'N/A',
        premium: 2566, renewalPremium: 2808, renewalPolicy: 'TWP4801199', renewalQuote: 'TWP4801199',
        pctChange: 9, dollarChange: 242, parentCarrier: 'AmTrust Financial Services',
        aiRisk: 'low',
        aiReasoning: {
            summary: 'Renewal bound at 9% — driven by payroll growth and minor industry rate adjustment.',
            factors: [
                'Dd Fuel & Market reported 11% payroll increase in annual audit',
                'TX retail/convenience class saw 7.2% loss cost adjustment from NCCI this year',
                'AmTrust applied 2.1% agency tier discount based on book performance',
                'Zero reportable claims in policy year — modifier remains favorable',
            ],
            recommendation: 'accept', retentionScore: 93,
        },
        pipelineStage: 'bound',
        automation: { status: 'none' },
        commands: [],
    },
];
const MOCK_RETENTION: RetentionRow[] = [
    {
        id: 'ret-1', expiredDate: '2026-04-15', number: 'HO-7821-TX-2024',
        insured: 'Morales, Patricia', lineOfBusiness: 'Homeowners',
        carrier: 'Universal P&C', mga: 'N/A', premium: 1842,
        outcome: 'cancelled', aiShouldRetain: true, aiConfidence: 87,
        aiAnalysis: 'Policy was cancelled and a new Homeowners policy with Citizens Property was bound 7 days later for the same insured — AI assessment indicates this was a rate-driven competitive rewrite, not a voluntary departure.',
        aiFactors: [
            'New Citizens Property homeowners policy (CIT-HO-2026-00221) detected for same insured 7 days post-cancellation',
            'Universal P&C filed 18.4% rate increase in TX — likely triggered a competitive shop',
            'No claim history on this policy — clean exit, purely rate-driven',
            'Insured has 6-year agency tenure — relationship may still be salvageable via win-back campaign',
        ],
        evidence: { sameLoBDifferentCarrier: { carrier: 'Citizens Property Ins', policyNumber: 'CIT-HO-2026-00221', effectiveDate: '2026-04-22', estimatedPremium: 1590 } },
        lostReason: 'rewritten_away',
    },
    {
        id: 'ret-2', expiredDate: '2026-03-31', number: '7039187422',
        insured: 'BlueSky Logistics LLC', lineOfBusiness: 'Commercial Auto',
        carrier: 'Progressive Commercial', mga: 'N/A', premium: 4210,
        outcome: 'non_renewed', aiShouldRetain: true, aiConfidence: 72,
        aiAnalysis: 'Progressive did not renew after one at-fault accident. Three alternative carriers accept this risk class. No pre-expiration market shop was initiated — this was a preventable loss.',
        aiFactors: [
            'Progressive non-renewed after 1 at-fault accident exceeding their internal renewal threshold',
            'Employers, Nationwide, and State Auto all accept this NAICS class with similar loss history',
            'No shop quote was ordered in the 45-day pre-expiration window per AMS records',
            'Insured contacted agency 45 days prior — escalation was not triggered',
        ],
        evidence: {},
        lostReason: 'carrier_non_renewed',
    },
    {
        id: 'ret-3', expiredDate: '2026-04-30', number: 'PAP-332-2024',
        insured: 'Dominguez, Carlos', lineOfBusiness: 'Personal Auto',
        carrier: 'Safeco Ins Co of IL', mga: 'N/A', premium: 1124,
        outcome: 'cancelled', aiShouldRetain: false, aiConfidence: 92,
        aiAnalysis: 'Client-requested cancellation — insured relocated to Colorado. TX policy no longer covers the primary garaging address. This is a legitimate relocation exit; no competing policy detected in TX.',
        aiFactors: [
            'Cancellation reason on file: "Insured relocated to Colorado" — confirmed via DMV notification',
            'No competing policy detected in TX — confirms clean relocation exit',
            'Agency does not currently hold CO market access for this carrier tier',
            'Client relationship intact — consider proactive referral to a partner agency in CO',
        ],
        evidence: {},
        lostReason: 'client_cancelled',
    },
    {
        id: 'ret-4', expiredDate: '2026-05-01', number: 'WC-CNA-77441',
        insured: 'Precision Metal Works', lineOfBusiness: "Worker's Compensation",
        carrier: 'Continental Casualty (CNA)', mga: 'BTIS', premium: 6380,
        outcome: 'expired', aiShouldRetain: true, aiConfidence: 94,
        aiAnalysis: 'Policy expired without renewal or replacement. An active WC policy with Employers Preferred was detected for the same FEIN effective 9 days after expiration — strongly indicates this account was written away to a competitor.',
        aiFactors: [
            'Employers Preferred WC policy for same FEIN found effective 2026-05-10 — 9 days post-expiration',
            'CNA quoted a 22% increase; Employers came in approximately 14% below CNA renewal',
            'No shop quote was ordered by the agency before the CNA renewal deadline',
            'This represents a $6,380 annual premium loss — high-priority win-back candidate',
        ],
        evidence: { sameLoBDifferentCarrier: { carrier: 'Employers Preferred', policyNumber: 'EIGS-WC-2026-9944', effectiveDate: '2026-05-10', estimatedPremium: 5490 } },
        lostReason: 'rewritten_away',
    },
    {
        id: 'ret-5', expiredDate: '2026-04-01', number: 'SSH-HO-2210-25',
        insured: 'Chen, Wei', lineOfBusiness: 'Homeowners',
        carrier: 'Lyndon Southern Insurance', mga: 'Savvi Insurance Group', premium: 2190,
        outcome: 'non_renewed', aiShouldRetain: true, aiConfidence: 81,
        aiAnalysis: 'Lyndon Southern non-renewed due to coastal wind exposure. The agency had 90 days advance notice and Savvi MGA has three carriers that cover this ZIP. No alternative market was shopped — this was an avoidable loss.',
        aiFactors: [
            'Lyndon Southern sent non-renewal notice 90 days prior — standard coastal process',
            'Savvi MGA has active placements for this ZIP via HCI, FedNat, and Slide Insurance',
            'Insured has zero claims in 5 years — strong placement profile for alternative markets',
            'No AMS record of replacement shopping being initiated after the non-renewal notice',
        ],
        evidence: {},
        lostReason: 'carrier_non_renewed',
    },
];
const PIPELINE_STAGES_LIST: {
    id: PipelineStage;
    short: string;
}[] = [
    { id: 'not_started', short: 'Start' },
    { id: 'outreach', short: 'Outreach' },
    { id: 'quoting', short: 'Quoting' },
    { id: 'negotiation', short: 'Negotiate' },
    { id: 'pending_bind', short: 'Pending' },
    { id: 'bound', short: 'Bound' },
];
function fmt$(n: number) {
    return n === 0 ? '$0.00' : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function daysUntil(dateStr: string) {
    return Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86400000);
}
function now() {
    return new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}
function daysBetween(a: string, b: string): number {
    return Math.round(Math.abs(new Date(b).getTime() - new Date(a).getTime()) / 86400000);
}
function generateRenewalResponse(query: string): string {
    const q = query.toLowerCase();
    const data = MOCK_RENEWALS;
    const highRisk = data.filter(r => r.aiRisk === 'high');
    const noRenewal = data.filter(r => !r.renewalPolicy && !r.renewalQuote);
    const shopCandidates = data.filter(r => r.aiReasoning.recommendation === 'shop');
    const withIncrease = data.filter(r => r.pctChange > 0);
    const avgInc = withIncrease.length
        ? (withIncrease.reduce((s, r) => s + r.pctChange, 0) / withIncrease.length).toFixed(1)
        : '0';
    const policyMatch = data.find(r => r.insured.toLowerCase().split(',')[0].trim().split(' ').some(w => w.length > 3 && q.includes(w.toLowerCase()))
        || q.includes(r.number.toLowerCase()));
    if (policyMatch) {
        const rec = policyMatch.aiReasoning.recommendation;
        const label = rec === 'accept' ? 'Accept Renewal' : rec === 'shop' ? 'Shop Market' : 'Contact Insured';
        return `**${policyMatch.insured}** — ${policyMatch.number}\n\n${policyMatch.aiReasoning.summary}\n\n**Recommendation:** ${label}  ·  Retention score: ${policyMatch.aiReasoning.retentionScore}%${policyMatch.aiReasoning.alternativeCarriers ? `\n\n**Alternatives:** ${policyMatch.aiReasoning.alternativeCarriers.join(', ')}` : ''}`;
    }
    if (q.includes('high risk') || q.includes('urgent') || q.includes('flag') || q.includes('alert')) {
        return `**${highRisk.length} high-risk policies** need immediate attention:\n\n${highRisk.map(r => `• **${r.insured}** (${r.number}) — ${r.aiReasoning.summary}`).join('\n\n')}`;
    }
    if (q.includes('no renewal') || q.includes('outreach') || q.includes('contact') || q.includes('missing')) {
        return `**${noRenewal.length} policies** have no renewal received:\n\n${noRenewal.map(r => `• **${r.insured}** (${r.number}) — expires ${r.expDate}`).join('\n')}\n\nI can draft personalized outreach emails for these clients. Just say "draft outreach" to proceed.`;
    }
    if (q.includes('shop') || q.includes('market') || q.includes('quote') || q.includes('compare') || q.includes('alternative')) {
        return `**${shopCandidates.length} shop-market candidates** identified:\n\n${shopCandidates.map(r => `• **${r.insured}** — +${r.pctChange}% increase · Try: ${r.aiReasoning.alternativeCarriers?.join(', ') || 'check market'}`).join('\n\n')}`;
    }
    if (q.includes('expir') || q.includes('this week') || q.includes('today') || q.includes('soon')) {
        const soon = data.filter(r => daysUntil(r.expDate) <= 7);
        return `**${soon.length} policies expire within 7 days:**\n\n${soon.map(r => { const d = daysUntil(r.expDate); return `• **${r.insured}** (${r.number}) — ${d <= 0 ? 'expires today' : `in ${d} day${d === 1 ? '' : 's'}`}`; }).join('\n')}`;
    }
    if (q.includes('increase') || q.includes('premium') || q.includes('price') || q.includes('cost')) {
        const sorted = [...data].filter(r => r.pctChange > 0).sort((a, b) => b.pctChange - a.pctChange);
        return `**Highest premium increases** this cycle:\n\n${sorted.slice(0, 4).map(r => `• **${r.insured}** — +${r.pctChange}% (+${fmt$(r.dollarChange)})`).join('\n')}\n\nBook average: **+${avgInc}%**`;
    }
    if (q.includes('priorit') || q.includes('focus') || q.includes('what should') || q.includes('next') || q.includes('recommend')) {
        return `**Recommended action order:**\n\n1. **${highRisk[0]?.insured}** — expires today, no renewal received\n2. **${highRisk[1]?.insured}** — WC expiring in 10 days, no renewal in system\n3. **${shopCandidates[0]?.insured}** — +${shopCandidates[0]?.pctChange}% increase, shop Progressive & Travelers\n4. **${highRisk[2]?.insured}** — engagement gap, 47 days since last contact\n\nStart with outreach, then generate shop quotes for the premium increase cases.`;
    }
    if (q.includes('summary') || q.includes('overview') || q.includes('status') || q.includes('how many') || q.includes('total')) {
        return `**Renewal Book Overview:**\n\n• **${data.length}** total policies in this cycle\n• **${highRisk.length}** flagged high-risk\n• **${noRenewal.length}** with no renewal received\n• **${shopCandidates.length}** shop-market candidates\n• **${data.filter(r => r.aiReasoning.recommendation === 'accept').length}** ready to accept\n• Book-average premium increase: **+${avgInc}%**`;
    }
    if (q.includes('accept') || q.includes('approve') || q.includes('bind')) {
        const accept = data.filter(r => r.aiReasoning.recommendation === 'accept');
        return `**${accept.length} policies ready to accept:**\n\n${accept.map(r => `• **${r.insured}** (${r.number}) — ${r.pctChange > 0 ? `+${r.pctChange}%` : 'no change'} · Retention: ${r.aiReasoning.retentionScore}%`).join('\n')}`;
    }
    return `I've analyzed your full renewal book. **${data.length} policies** are coming up — **${highRisk.length} high-risk**, **${shopCandidates.length} to shop**, and **${noRenewal.length} with no renewal received**.\n\nTry asking: "prioritize my renewals", "show shop candidates", or click any policy row for a detailed breakdown.`;
}
function renderChatContent(text: string): React.ReactNode {
    return text.split('\n').map((line, i, arr) => {
        const parts = line.split(/(\*\*[^*]+\*\*)/g);
        return (<React.Fragment key={i}>
        {parts.map((p, j) => p.startsWith('**') && p.endsWith('**')
                ? <strong key={j}>{p.slice(2, -2)}</strong>
                : <span key={j}>{p}</span>)}
        {i < arr.length - 1 && <br />}
      </React.Fragment>);
    });
}
const StatusBadge: React.FC<{
    status: PolicyStatus;
}> = ({ status }) => {
    const m: Record<PolicyStatus, string> = { Active: 'rc-badge--active', Renewed: 'rc-badge--renewed', Cancelled: 'rc-badge--cancelled', Archived: 'rc-badge--archived' };
    return <span className={`rc-badge ${m[status]}`}>{status}</span>;
};
const RiskBadge: React.FC<{
    risk: AiRisk;
}> = ({ risk }) => {
    const m: Record<AiRisk, string> = { low: 'rc-risk--low', medium: 'rc-risk--medium', high: 'rc-risk--high' };
    const icons: Record<AiRisk, React.ReactNode> = { low: <FiCheckCircle size={10}/>, medium: <FiActivity size={10}/>, high: <FiAlertTriangle size={10}/> };
    return <span className={`rc-risk ${m[risk]}`}>{icons[risk]}{risk.charAt(0).toUpperCase() + risk.slice(1)}</span>;
};
const ChangeBadge: React.FC<{
    pct: number;
    dollar: number;
    onClick: () => void;
    expanded: boolean;
}> = ({ pct, dollar, onClick, expanded }) => {
    const cls = pct === 0 ? 'rc-change--neutral' : pct < 6 ? 'rc-change--low' : pct < 15 ? 'rc-change--med' : 'rc-change--high';
    const icon = pct === 0 ? <FiMinus size={11}/> : pct > 0 ? <FiChevronUp size={11}/> : <FiChevronDown size={11}/>;
    return (<button className={`rc-change ${cls} ${expanded ? 'rc-change--expanded' : ''}`} onClick={onClick}>
      {icon}<span>{pct}%</span><span className="rc-change__dollar">{fmt$(dollar)}</span><FiZap size={10} className="rc-change__ai-icon"/>
    </button>);
};
const RecommendationChip: React.FC<{
    rec: AiRecommendation;
}> = ({ rec }) => {
    const config: Record<AiRecommendation, {
        label: string;
        cls: string;
        icon: React.ReactNode;
    }> = {
        accept: { label: 'Accept Renewal', cls: 'rc-rec--accept', icon: <FiCheckCircle size={12}/> },
        shop: { label: 'Shop Market', cls: 'rc-rec--shop', icon: <FiBarChart2 size={12}/> },
        contact: { label: 'Contact Insured', cls: 'rc-rec--contact', icon: <FiMessageSquare size={12}/> },
    };
    const { label, cls, icon } = config[rec];
    return <span className={`rc-rec ${cls}`}>{icon}{label}</span>;
};
const RetentionBar: React.FC<{
    score: number;
}> = ({ score }) => {
    const cls = score >= 80 ? 'rc-retention--high' : score >= 60 ? 'rc-retention--mid' : 'rc-retention--low';
    return (<div className="rc-retention">
      <span className="rc-retention__label">Retention Score</span>
      <div className="rc-retention__track">
        <motion.div className={`rc-retention__fill ${cls}`} initial={{ width: 0 }} animate={{ width: `${score}%` }} transition={{ duration: 0.6, ease: 'easeOut' }}/>
      </div>
      <span className="rc-retention__val">{score}%</span>
    </div>);
};
const autoBarConfig: Record<AutomationStatus, {
    label: string;
    cls: string;
    icon: React.ReactNode;
}> = {
    none: { label: 'No automation', cls: '', icon: <FiMinus size={9}/> },
    monitoring: { label: 'Monitoring', cls: 'rc-wbar-pill--cyan', icon: <FiActivity size={9}/> },
    drip: { label: 'Drip', cls: 'rc-wbar-pill--purple', icon: <FiRepeat size={9}/> },
    quote_request: { label: 'Quoting', cls: 'rc-wbar-pill--yellow', icon: <FiPlay size={9}/> },
    follow_up: { label: 'Follow-up', cls: 'rc-wbar-pill--orange', icon: <FiPlay size={9}/> },
    full_auto: { label: 'Full Auto', cls: 'rc-wbar-pill--green', icon: <FiZap size={9}/> },
};
const AutoDetail: React.FC<{
    row: RenewalRow;
}> = ({ row }) => {
    const { label, cls, icon } = autoBarConfig[row.automation.status];
    const actions = [
        row.automation.lastAction,
        (row.automation.actionsCount ?? 0) > 0 ? `${row.automation.actionsCount} total actions executed` : null,
        row.automation.status === 'drip' ? 'Next: Follow-up email scheduled in 2 days' : null,
        row.automation.status === 'quote_request' ? 'Awaiting quote response from carrier portal' : null,
        row.automation.status === 'follow_up' ? 'Follow-up call queued for tomorrow morning' : null,
        row.automation.status === 'full_auto' ? 'AI managing end-to-end renewal workflow' : null,
        row.automation.status === 'monitoring' ? 'Rate alerts active — no action needed yet' : null,
    ].filter(Boolean) as string[];
    return (<div className="rc-wbar-detail-inner">
      <div className="rc-wbar-detail-hdr">
        <FiCpu size={11}/><span>Automation Center</span>
        {row.automation.status !== 'none' && (<span className={`rc-wbar-pill ${cls}`} style={{ marginLeft: 'auto' }}>{icon}{label}</span>)}
      </div>
      {row.automation.status === 'none' ? (<div className="rc-wbar-ai-suggest">
          <FiCpu size={11} className="rc-wbar-ai-suggest__icon"/>
          <div>
            <p className="rc-wbar-ai-suggest__text">No automation active for this policy.</p>
            <p className="rc-wbar-ai-suggest__text">Configure a workflow to handle outreach, quoting, and follow-ups automatically.</p>
          </div>
          <button className="rc-wbar-ai-cta"><FiZap size={9}/>Set up Automation</button>
        </div>) : (<ul className="rc-wbar-detail-list">
          {actions.map((a, i) => <li key={i}><FiCheckCircle size={9}/>{a}</li>)}
        </ul>)}
    </div>);
};
const STAGE_DETAIL: Record<PipelineStage, {
    label: string;
    desc: string;
    next: string;
}> = {
    not_started: { label: 'Not Started', desc: 'No action taken on this renewal yet.', next: 'Send initial outreach and review current coverage terms.' },
    outreach: { label: 'Outreach', desc: 'Initial contact initiated with insured.', next: 'Confirm intent — initiate market review or re-quote.' },
    quoting: { label: 'Quoting', desc: 'Gathering quotes from available markets.', next: 'Compare options and prepare renewal proposal for insured.' },
    negotiation: { label: 'Negotiating', desc: 'Proposal presented — awaiting insured decision.', next: 'Follow up and resolve any objections before deadline.' },
    pending_bind: { label: 'Pending Bind', desc: 'Insured approved — awaiting bind confirmation from carrier.', next: 'Submit bind request and confirm effective date in AMS.' },
    bound: { label: 'Bound', desc: 'Policy successfully renewed and bound.', next: 'Deliver documents to insured and close AMS record.' },
    lost: { label: 'Lost', desc: 'Account was not retained this cycle.', next: 'Log loss reason and initiate win-back campaign if applicable.' },
};
const PipelineDetail: React.FC<{
    row: RenewalRow;
}> = ({ row }) => {
    const pipelineIdx = PIPELINE_STAGES_LIST.findIndex(s => s.id === row.pipelineStage);
    const isLost = row.pipelineStage === 'lost';
    const detail = STAGE_DETAIL[row.pipelineStage];
    return (<div className="rc-wbar-detail-inner">
      <div className="rc-wbar-detail-hdr">
        <FiGitBranch size={11}/><span>Renewal Pipeline</span>
        <span className={`rc-wbar-pill ${isLost ? 'rc-wbar-pill--orange' : 'rc-wbar-pill--purple'}`} style={{ marginLeft: 'auto' }}>
          {detail.label}
        </span>
      </div>
      <div className="rc-wbar-pipe-full">
        {PIPELINE_STAGES_LIST.map((s, i) => (<React.Fragment key={s.id}>
            <div className={`rc-wbar-pfull-step${i < pipelineIdx ? ' rc-wbar-pfull-step--done' : i === pipelineIdx ? ' rc-wbar-pfull-step--active' : ' rc-wbar-pfull-step--future'}`}>
              <div className="rc-wbar-pfull-dot">
                {i < pipelineIdx ? <FiCheck size={7}/> : i === pipelineIdx ? <FiPlay size={6}/> : null}
              </div>
              <span className="rc-wbar-pfull-label">{s.short}</span>
            </div>
            {i < PIPELINE_STAGES_LIST.length - 1 && (<div className={`rc-wbar-pfull-line${i < pipelineIdx ? ' rc-wbar-pfull-line--done' : ''}`}/>)}
          </React.Fragment>))}
      </div>
      <div className="rc-wbar-detail-pipeline-info">
        <p className="rc-wbar-detail-empty">{detail.desc}</p>
        <div className="rc-wbar-detail-pipeline-next">
          <FiChevronRight size={9}/><span>{detail.next}</span>
        </div>
      </div>
      <div className="rc-wbar-pfull-proposal">
        {(row.renewalQuote !== null || row.opportunity?.stage === 'Proposal Sent')
            ? <span className="rc-wbar-proposal-badge rc-wbar-proposal-badge--ready"><FiSend size={9}/>Proposal ready</span>
            : <span className="rc-wbar-proposal-badge"><span className="rc-wbar-ai-cta-inline" onClick={e => e.stopPropagation()}><FiZap size={9}/>Generate Proposal with AI</span></span>}
      </div>
    </div>);
};
const CmdDetail: React.FC<{
    row: RenewalRow;
    allCommands: PolicyCommand[];
    onCreateCommand: (e: React.MouseEvent) => void;
}> = ({ row, allCommands, onCreateCommand }) => (<div className="rc-wbar-detail-inner">
    <div className="rc-wbar-detail-hdr">
      <FiTerminal size={11}/><span>AI Commands</span>
      <button className="rc-cmd-create rc-cmd-create--sm" style={{ marginLeft: 'auto' }} onClick={onCreateCommand}>
        <FiPlusCircle size={9}/>New command
      </button>
    </div>
    {allCommands.length === 0 ? (<div className="rc-wbar-ai-suggest">
        <FiTerminal size={11} className="rc-wbar-ai-suggest__icon"/>
        <div>
          <p className="rc-wbar-ai-suggest__text">No AI commands yet.</p>
          <p className="rc-wbar-ai-suggest__text">Commands let you direct AI to shop quotes, draft outreach, schedule follow-ups, or bind docs.</p>
        </div>
        <button className="rc-wbar-ai-cta" onClick={onCreateCommand}><FiPlusCircle size={9}/>Create First Command</button>
      </div>) : (<ul className="rc-wbar-detail-list">
        {allCommands.map(cmd => (<li key={cmd.id} className={`rc-cmd-status--${cmd.status}`}>
            {cmd.status === 'completed' ? <FiCheckSquare size={9}/> : cmd.status === 'active' ? <FiPlay size={9}/> : <FiClock size={9}/>}
            <span>{cmd.title}</span>
            <span className="rc-wbar-detail-cmd-s">{{ active: 'open', pending: 'queued', completed: 'done', failed: 'failed' }[cmd.status]}</span>
          </li>))}
      </ul>)}
  </div>);
const OpportunitiesDetail: React.FC<{
    row: RenewalRow;
    accountRows: RenewalRow[];
}> = ({ row, accountRows }) => {
    const [tab, setTab] = React.useState<'market' | 'upsell'>('market');
    const carriers = MARKET_APPETITE[row.lineOfBusiness] ?? [];
    const altCarriers = row.aiReasoning.alternativeCarriers ?? [];
    const lobsOnFile = useMemo(() => new Set(accountRows.map(r => r.lineOfBusiness)), [accountRows]);
    const upsellOpps = useMemo(() => UPSELL_RULES.filter(rule => rule.triggerLob.some(l => lobsOnFile.has(l)) && !rule.blockedBy.some(l => lobsOnFile.has(l))), [lobsOnFile]);
    return (<div className="rc-wbar-detail-inner">
      <div className="rc-wbar-detail-hdr">
        <FiFlag size={11}/><span>Appetite — {row.insured}</span>
      </div>
      <div className="rc-wbar-opps-tabs">
        <button className={`rc-wbar-opps-tab${tab === 'market' ? ' rc-wbar-opps-tab--active' : ''}`} onClick={() => setTab('market')}>
          <FiBarChart2 size={9}/>Market Appetite
          {(carriers.length + altCarriers.length) > 0 && (<span className="rc-wbar-opps-tab-count">{carriers.length + altCarriers.length}</span>)}
        </button>
        <button className={`rc-wbar-opps-tab${tab === 'upsell' ? ' rc-wbar-opps-tab--active' : ''}`} onClick={() => setTab('upsell')}>
          <FiFlag size={9}/>Cross-sell
          {upsellOpps.length > 0 && (<span className="rc-wbar-opps-tab-count">{upsellOpps.length}</span>)}
        </button>
      </div>

      {tab === 'market' && (carriers.length === 0 && altCarriers.length === 0 ? (<div className="rc-wbar-ai-suggest">
            <FiBarChart2 size={11} className="rc-wbar-ai-suggest__icon"/>
            <div>
              <p className="rc-wbar-ai-suggest__text">No market data loaded for {row.lineOfBusiness}.</p>
              <p className="rc-wbar-ai-suggest__text">Ask AI to research available carriers and appetite for this account.</p>
            </div>
            <button className="rc-wbar-ai-cta"><FiZap size={9}/>Research Markets with AI</button>
          </div>) : (<>
            <ul className="rc-market-list">
              {carriers.map(c => (<li key={c.carrier} className="rc-market-item">
                  <span className="rc-market-item__name">{c.carrier}</span>
                  <span className={`rc-market-item__tier rc-market-item__tier--${c.tier}`}>{c.tier}</span>
                  <span className="rc-market-item__note">{c.note}</span>
                </li>))}
              {altCarriers.map(c => (<li key={c} className="rc-market-item">
                  <span className="rc-market-item__name">{c}</span>
                  <span className="rc-market-item__tier rc-market-item__tier--secondary">AI rec</span>
                </li>))}
            </ul>
            <button className="rc-market-cta">
              <FiBarChart2 size={12}/> Get Quotes via Rate/Win
            </button>
          </>))}

      {tab === 'upsell' && (upsellOpps.length === 0 ? (<div className="rc-wbar-ai-suggest">
            <FiTarget size={11} className="rc-wbar-ai-suggest__icon"/>
            <div>
              <p className="rc-wbar-ai-suggest__text">No cross-sell gaps identified for lines currently on file.</p>
              <p className="rc-wbar-ai-suggest__text">Ask AI to analyze this account for additional coverage opportunities.</p>
            </div>
            <button className="rc-wbar-ai-cta"><FiZap size={9}/>Analyze Account with AI</button>
          </div>) : (<ul className="rc-upsell-list">
            {upsellOpps.map(opp => (<li key={opp.missingLob} className="rc-upsell-item">
                <span className="rc-upsell-item__lob">{opp.missingLob}</span>
                <span className="rc-upsell-item__reason">{opp.reason}</span>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                  <span className="rc-upsell-item__meta">Est. {opp.estimatedPremium}</span>
                  <button className="rc-cmd-create rc-cmd-create--sm rc-upsell-item__action">
                    <FiPlusCircle size={9}/>Create AI Command
                  </button>
                </div>
              </li>))}
          </ul>))}
    </div>);
};
const fmt$_ = (n: number) => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const AccountDetail: React.FC<{
    row: RenewalRow;
    accountRows: RenewalRow[];
}> = ({ row, accountRows }) => {
    const totalCurrent = accountRows.reduce((s, r) => s + r.premium, 0);
    const totalRenewal = accountRows.reduce((s, r) => s + r.renewalPremium, 0);
    const totalPct = totalCurrent > 0 ? ((totalRenewal - totalCurrent) / totalCurrent) * 100 : 0;
    const stageShort = (stage: PipelineStage) => PIPELINE_STAGES_LIST.find(s => s.id === stage)?.short ?? stage;
    return (<div className="rc-wbar-detail-inner">
      <div className="rc-wbar-detail-hdr">
        <FiUsers size={11}/><span>Account Overview — {row.insured}</span>
        <span className="rc-wbar-pill rc-wbar-pill--purple" style={{ marginLeft: 'auto' }}>
          {accountRows.length} {accountRows.length === 1 ? 'policy' : 'policies'} renewing
        </span>
      </div>
      <div className="rc-account-rows">
        <div className="rc-account-row rc-account-row--header">
          <span>Line of Business</span>
          <span>Policy #</span>
          <span>Current</span>
          <span>Renewal</span>
          <span>Change</span>
          <span>Stage</span>
        </div>
        {accountRows.map(r => {
            const pct = r.pctChange;
            const sign = pct >= 0 ? '+' : '';
            return (<div key={r.id} className={`rc-account-row${r.id === row.id ? ' rc-account-row--current' : ''}`}>
              <span>{r.lineOfBusiness}</span>
              <span className="rc-account-row__num">{r.number}</span>
              <span>{fmt$_(r.premium)}</span>
              <span>{fmt$_(r.renewalPremium)}</span>
              <span className={`rc-account-row__pct${pct > 5 ? ' rc-account-row__pct--up' : pct < -1 ? ' rc-account-row__pct--down' : ''}`}>
                {sign}{pct.toFixed(1)}%
              </span>
              <span className="rc-account-row__stage">{stageShort(r.pipelineStage)}</span>
            </div>);
        })}
        <div className="rc-account-row rc-account-totals">
          <span>Total</span>
          <span></span>
          <span>{fmt$_(totalCurrent)}</span>
          <span>{fmt$_(totalRenewal)}</span>
          <span className={`rc-account-row__pct${totalPct > 5 ? ' rc-account-row__pct--up' : totalPct < -1 ? ' rc-account-row__pct--down' : ''}`}>
            {totalPct >= 0 ? '+' : ''}{totalPct.toFixed(1)}%
          </span>
          <span></span>
        </div>
      </div>
    </div>);
};
const WorkflowBar: React.FC<{
    row: RenewalRow;
    allCommands: PolicyCommand[];
    accountCount: number;
    accountTotal: number;
    accountRows: RenewalRow[];
    onCreateCommand: (e: React.MouseEvent) => void;
}> = ({ row, allCommands, onCreateCommand, accountRows, accountCount }) => {
    const [openSection, setOpenSection] = useState<'pipeline' | 'auto' | 'cmd' | 'opps' | 'account' | null>(null);
    const pipelineIdx = PIPELINE_STAGES_LIST.findIndex(s => s.id === row.pipelineStage);
    const isLost = row.pipelineStage === 'lost';
    const activeCount = allCommands.filter(c => c.status === 'active' || c.status === 'pending').length;
    const { label: autoLabel, cls: autoCls, icon: autoIcon } = autoBarConfig[row.automation.status];
    const toggle = (s: 'pipeline' | 'auto' | 'cmd' | 'opps' | 'account', e: React.MouseEvent) => {
        e.stopPropagation();
        setOpenSection(p => p === s ? null : s);
    };
    const marketCarrierCount = (MARKET_APPETITE[row.lineOfBusiness] ?? []).length + (row.aiReasoning.alternativeCarriers ?? []).length;
    const lobsOnFile = new Set(accountRows.map(r => r.lineOfBusiness));
    const upsellCount = UPSELL_RULES.filter(rule => rule.triggerLob.some(l => lobsOnFile.has(l)) && !rule.blockedBy.some(l => lobsOnFile.has(l))).length;
    const totalOpps = marketCarrierCount + upsellCount;
    const hasMultiPolicy = accountCount > 1;
    return (<div className="rc-workflow-bar" onClick={e => e.stopPropagation()}>

      
      <div className="rc-wbar-strip">

        
        <button className={`rc-wbar-section rc-wbar-section--btn${openSection === 'pipeline' ? ' rc-wbar-section--open' : ''}`} onClick={e => toggle('pipeline', e)}>
          <span className="rc-wbar-label">Pipeline</span>
          <div className="rc-wbar-pipe">
            {isLost ? <span className="rc-wbar-lost">Lost</span> : (<>
                {PIPELINE_STAGES_LIST.map((s, i) => (<React.Fragment key={s.id}>
                    <div className={`rc-wbar-dot ${i < pipelineIdx ? 'rc-wbar-dot--done' : i === pipelineIdx ? 'rc-wbar-dot--active' : 'rc-wbar-dot--future'}`}/>
                    {i < PIPELINE_STAGES_LIST.length - 1 && <div className={`rc-wbar-line ${i < pipelineIdx ? 'rc-wbar-line--done' : ''}`}/>}
                  </React.Fragment>))}
                <span className="rc-wbar-stage">{PIPELINE_STAGES_LIST[pipelineIdx]?.short ?? '—'}</span>
              </>)}
          </div>
        </button>

        <span className="rc-wbar-sep"/>

        
        <button className={`rc-wbar-section rc-wbar-section--btn${openSection === 'auto' ? ' rc-wbar-section--open' : ''}`} onClick={e => toggle('auto', e)}>
          <span className="rc-wbar-label">Automation</span>
          {row.automation.status !== 'none' ? (<div className={`rc-wbar-pill ${autoCls}`}>
              {autoIcon}<span>{autoLabel}</span>
              {(row.automation.actionsCount ?? 0) > 0 && <span className="rc-wbar-count">{row.automation.actionsCount}</span>}
            </div>) : <span className="rc-wbar-ask-ai" onClick={e => { e.stopPropagation(); toggle('auto', e); }}><FiZap size={9}/>Set up</span>}
        </button>

        <span className="rc-wbar-sep"/>

        
        <button className={`rc-wbar-section rc-wbar-section--btn${openSection === 'cmd' ? ' rc-wbar-section--open' : ''}`} onClick={e => toggle('cmd', e)}>
          <span className="rc-wbar-label">AI Commands</span>
          {allCommands.length === 0
            ? <span className="rc-wbar-ask-ai"><FiZap size={9}/>Ask AI</span>
            : <div className="rc-wbar-pill rc-wbar-pill--cmd-exists">
                <FiTerminal size={9}/>
                <span>{activeCount > 0 ? `${activeCount} open` : `${allCommands.length} done`}</span>
              </div>}
        </button>

        <span className="rc-wbar-sep"/>

        
        <button className={`rc-wbar-section rc-wbar-section--btn${openSection === 'opps' ? ' rc-wbar-section--open' : ''}`} onClick={e => toggle('opps', e)}>
          <span className="rc-wbar-label">Appetite</span>
          {totalOpps > 0
            ? <div className="rc-wbar-pill rc-wbar-pill--cyan">
                <FiFlag size={9}/>
                <span>{totalOpps} found</span>
              </div>
            : <span className="rc-wbar-ask-ai"><FiZap size={9}/>Ask AI</span>}
        </button>

        
        {hasMultiPolicy && (<>
            <span className="rc-wbar-sep"/>
            <button className={`rc-wbar-section rc-wbar-section--btn rc-wbar-section--account${openSection === 'account' ? ' rc-wbar-section--open' : ''}`} onClick={e => toggle('account', e)}>
              <span className="rc-wbar-label">Account</span>
              <div className="rc-wbar-pill rc-wbar-pill--purple">
                <FiFileText size={9}/>
                <span>{accountCount} policies</span>
              </div>
            </button>
          </>)}
      </div>

      
      <SafeAnimatePresence>
        {openSection && (<motion.div className="rc-wbar-detail" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}>
            {openSection === 'pipeline' && <PipelineDetail row={row}/>}
            {openSection === 'auto' && <AutoDetail row={row}/>}
            {openSection === 'cmd' && <CmdDetail row={row} allCommands={allCommands} onCreateCommand={e => { onCreateCommand(e); setOpenSection(null); }}/>}
            {openSection === 'opps' && <OpportunitiesDetail row={row} accountRows={accountRows}/>}
            {openSection === 'account' && <AccountDetail row={row} accountRows={accountRows}/>}
          </motion.div>)}
      </SafeAnimatePresence>
    </div>);
};
const WorkflowContext: React.FC<{
    row: RenewalRow;
    allCommands: PolicyCommand[];
    onCreateCommand: () => void;
    onViewCommands: () => void;
}> = ({ row, allCommands, onCreateCommand, onViewCommands }) => {
    const pipelineIdx = PIPELINE_STAGES_LIST.findIndex(s => s.id === row.pipelineStage);
    const isLost = row.pipelineStage === 'lost';
    const activeCommands = allCommands.filter(c => c.status === 'active' || c.status === 'pending');
    const { label: autoLabel, cls: autoCls, icon: autoIcon } = autoBarConfig[row.automation.status];
    return (<div className="rc-wctx">
      <div className="rc-wctx__header">
        <FiGitBranch size={12}/><span>Pipeline &amp; Workflow</span>
      </div>

      
      <div className="rc-wctx__pipeline">
        {isLost ? (<span className="rc-wbar-lost">Lost</span>) : (PIPELINE_STAGES_LIST.map((s, i) => (<React.Fragment key={s.id}>
              <div className={`rc-wctx-step ${i < pipelineIdx ? 'rc-wctx-step--done' : i === pipelineIdx ? 'rc-wctx-step--active' : ''}`}>
                <div className="rc-wctx-step__dot">
                  {i < pipelineIdx && <FiCheck size={7}/>}
                </div>
                <span className="rc-wctx-step__label">{s.short}</span>
              </div>
              {i < PIPELINE_STAGES_LIST.length - 1 && (<div className={`rc-wctx-step__line ${i < pipelineIdx ? 'rc-wctx-step__line--done' : ''}`}/>)}
            </React.Fragment>)))}
      </div>

      
      <div className="rc-wctx__cols">

        
        <div className="rc-wctx__card">
          <div className="rc-wctx__card-label"><FiCpu size={10}/>Automation Center</div>
          <div className={`rc-wctx__auto-badge ${autoCls}`}>
            {autoIcon}<span>{autoLabel}</span>
            {row.automation.actionsCount !== undefined && row.automation.actionsCount > 0 && (<span className="rc-wctx__auto-count">{row.automation.actionsCount} actions</span>)}
          </div>
          {row.automation.lastAction && (<div className="rc-wctx__card-sub">{row.automation.lastAction}</div>)}
        </div>

        
        <div className="rc-wctx__card">
          <div className="rc-wctx__card-label"><FiTarget size={10}/>CRM Opportunity</div>
          {row.opportunity ? (<>
              <div className="rc-wctx__opp-row">
                <span className="rc-opp-label">{row.opportunity.label}</span>
                <span className="rc-wctx__card-sub">{row.opportunity.stage}</span>
              </div>
              {row.opportunity.value > 0 && (<div className="rc-wctx__opp-val">{fmt$(row.opportunity.value)}</div>)}
            </>) : (<div className="rc-wctx__card-empty">No opportunity linked</div>)}
        </div>

        
        <div className="rc-wctx__card">
          <div className="rc-wctx__card-label"><FiTerminal size={10}/>Command Center</div>
          {allCommands.length === 0 ? (<button className="rc-cmd-create" onClick={onCreateCommand}>
              <FiPlusCircle size={11}/><span>Create Command</span>
            </button>) : (<div className="rc-wctx__commands">
              {allCommands.slice(0, 2).map(cmd => (<div key={cmd.id} className={`rc-wctx__cmd-row rc-cmd-status--${cmd.status}`}>
                  {cmd.status === 'completed' ? <FiCheckSquare size={10}/> : cmd.status === 'active' ? <FiPlay size={10}/> : <FiClock size={10}/>}
                  <span>{cmd.title}</span>
                </div>))}
              <div className="rc-wctx__cmd-footer">
                {allCommands.length > 2 && (<span className="rc-wctx__cmd-more">+{allCommands.length - 2} more</span>)}
                <button className="rc-cmd-create rc-cmd-create--sm" onClick={onCreateCommand}>
                  <FiPlusCircle size={9}/>
                </button>
                {activeCommands.length > 0 && (<button className="rc-cmd-view-btn" onClick={onViewCommands}>View all</button>)}
              </div>
            </div>)}
        </div>

      </div>
    </div>);
};
const AIReasoningPanel: React.FC<{
    row: RenewalRow;
    allCommands: PolicyCommand[];
    onViewSummary: () => void;
    onCreateCommand: () => void;
    onViewCommands: () => void;
}> = ({ row, allCommands, onViewSummary, onCreateCommand, onViewCommands }) => (<motion.div className="rc-reasoning" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3, ease: 'easeInOut' }}>
    <div className="rc-reasoning__inner">
      <div className="rc-reasoning__header">
        <div className="rc-reasoning__title"><FiCpu size={14}/><span>AI Analysis — {row.insured}</span></div>
        <RecommendationChip rec={row.aiReasoning.recommendation}/>
      </div>
      <p className="rc-reasoning__summary">{row.aiReasoning.summary}</p>
      <div className="rc-reasoning__factors">
        <span className="rc-reasoning__factors-label">Key Factors</span>
        <ul>
          {row.aiReasoning.factors.map((f, i) => (<motion.li key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }}>
              <FiChevronRight size={11}/>{f}
            </motion.li>))}
        </ul>
      </div>

      
      <WorkflowContext row={row} allCommands={allCommands} onCreateCommand={onCreateCommand} onViewCommands={onViewCommands}/>

      <div className="rc-reasoning__footer">
        <RetentionBar score={row.aiReasoning.retentionScore}/>
        {row.aiReasoning.alternativeCarriers && (<div className="rc-reasoning__alts">
            <span>Alternatives:</span>
            {row.aiReasoning.alternativeCarriers.map(c => <span key={c} className="rc-alt-chip">{c}</span>)}
          </div>)}
        {row.currentSnapshot && row.renewalSnapshot && (<button className="glass-btn rc-reasoning__compare-btn" onClick={onViewSummary}>
            <FiSearch size={13}/> View Policy Comparison
          </button>)}
      </div>
    </div>
  </motion.div>);
const PolicyComparisonModal: React.FC<{
    row: RenewalRow;
    onClose: () => void;
}> = ({ row, onClose }) => {
    const cur = row.currentSnapshot!;
    const ren = row.renewalSnapshot!;
    return (<SafeAnimatePresence>
      <motion.div className="rc-modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
        <motion.div className="rc-modal glass-card" initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} transition={{ duration: 0.25 }} onClick={e => e.stopPropagation()}>
          <div className="rc-modal__header">
            <div className="rc-modal__title"><FiFileText size={16}/><span>Policy Comparison — {row.insured}</span></div>
            <div className="rc-modal__meta"><span className="rc-modal__number">{row.number}</span><RiskBadge risk={row.aiRisk}/></div>
            <button className="rc-modal__close glass-btn" onClick={onClose}><FiX size={16}/></button>
          </div>
          <div className="rc-modal__body">
            <div className="rc-modal__ai-bar"><FiCpu size={13}/><span>{row.aiReasoning.summary}</span><RecommendationChip rec={row.aiReasoning.recommendation}/></div>
            <div className="rc-modal__cols">
              <div className="rc-modal__col rc-modal__col--current">
                <div className="rc-modal__col-header"><span className="rc-modal__col-label">Current Policy</span><span className="rc-modal__col-period">{cur.period}</span><span className="rc-badge rc-badge--active">Active</span></div>
                <div className="rc-modal__section">
                  <div className="rc-modal__field"><span>Line of Business</span><span>{cur.lineOfBusiness}</span></div>
                  <div className="rc-modal__field"><span>Carrier</span><span>{cur.carrier}</span></div>
                  <div className="rc-modal__field rc-modal__field--highlight"><span>Premium</span><span className="rc-modal__prem">{fmt$(cur.premium)}</span></div>
                  <div className="rc-modal__field"><span>Non Premium</span><span>{fmt$(cur.nonPremium)}</span></div>
                </div>
                <div className="rc-modal__section-title">Coverages</div>
                <div className="rc-modal__coverages">
                  <div className="rc-modal__cov-header"><span>Coverage</span><span>Limit 1</span><span>Premium</span></div>
                  {cur.coverages.map((c, i) => <div key={i} className="rc-modal__cov-row"><span>{c.coverage}</span><span>{c.limit1}</span><span>{c.premium ?? '—'}</span></div>)}
                </div>
                {cur.vehicles && <><div className="rc-modal__section-title">Vehicles ({cur.vehicles.length})</div>{cur.vehicles.map((v, i) => <div key={i} className="rc-modal__vehicle"><span>{v.year} {v.make} {v.model}</span><span className="rc-modal__vin">{v.vin}</span></div>)}</>}
                {cur.drivers && <><div className="rc-modal__section-title">Drivers ({cur.drivers.length})</div>{cur.drivers.map((d, i) => <div key={i} className="rc-modal__driver"><FiUsers size={12}/><span>{d.name}</span><span className="rc-modal__dob">DOB {d.dob}</span></div>)}</>}
              </div>
              <div className="rc-modal__col rc-modal__col--renewal">
                <div className="rc-modal__col-header"><span className="rc-modal__col-label">Renewal Policy</span><span className="rc-modal__col-period">{ren.period}</span><span className="rc-badge rc-badge--renewed">Renewing</span></div>
                <div className="rc-modal__section">
                  <div className="rc-modal__field"><span>Line of Business</span><span>{ren.lineOfBusiness}</span></div>
                  <div className="rc-modal__field"><span>Carrier</span><span>{ren.carrier}</span></div>
                  <div className="rc-modal__field rc-modal__field--highlight rc-modal__field--increased"><span>Premium</span><span className="rc-modal__prem">{fmt$(ren.premium)}{ren.premium > cur.premium && <span className="rc-modal__prem-diff">+{fmt$(ren.premium - cur.premium)}</span>}</span></div>
                  <div className="rc-modal__field"><span>Non Premium</span><span>{fmt$(ren.nonPremium)}</span></div>
                </div>
                <div className="rc-modal__section-title">Coverages</div>
                <div className="rc-modal__coverages">
                  <div className="rc-modal__cov-header"><span>Coverage</span><span>Limit 1</span><span>Premium</span></div>
                  {ren.coverages.map((c, i) => { const isNew = !cur.coverages.find(x => x.coverage === c.coverage); return <div key={i} className={`rc-modal__cov-row ${isNew ? 'rc-modal__cov-row--new' : ''}`}><span>{c.coverage}{isNew && <span className="rc-new-tag">New</span>}</span><span>{c.limit1}</span><span>{c.premium ?? '—'}</span></div>; })}
                </div>
                {ren.vehicles && <><div className="rc-modal__section-title">Vehicles ({ren.vehicles.length})</div>{ren.vehicles.map((v, i) => <div key={i} className="rc-modal__vehicle"><span>{v.year} {v.make} {v.model}</span><span className="rc-modal__vin">{v.vin}</span></div>)}</>}
                {ren.drivers && <><div className="rc-modal__section-title">Drivers ({ren.drivers.length})</div>{ren.drivers.map((d, i) => <div key={i} className="rc-modal__driver"><FiUsers size={12}/><span>{d.name}</span><span className="rc-modal__dob">DOB {d.dob}</span></div>)}</>}
                {ren.notes && <div className="rc-modal__note"><FiAlertTriangle size={12}/>{ren.notes}</div>}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </SafeAnimatePresence>);
};
const RefreshButton: React.FC<{
    isRefreshing: boolean;
    secsSince: number;
    onRefresh: () => void;
}> = ({ isRefreshing, secsSince, onRefresh }) => {
    const TOTAL = 30;
    const r = 10;
    const circ = 2 * Math.PI * r;
    const progress = Math.min(secsSince / TOTAL, 1);
    const offset = circ * (1 - progress);
    return (<div className="rc-refresh-wrap">
      <button className={`rc-refresh-btn${isRefreshing ? ' rc-refresh-btn--active' : ''}`} onClick={onRefresh} title={isRefreshing ? 'Refreshing…' : `Last refreshed ${secsSince}s ago — click to refresh`}>
        <svg className="rc-refresh-ring" viewBox="0 0 26 26" fill="none">
          <circle cx="13" cy="13" r={r} strokeWidth="1.5" stroke="rgba(255,255,255,0.08)"/>
          <circle cx="13" cy="13" r={r} strokeWidth="1.5" stroke={isRefreshing ? 'rgba(34,211,238,0.7)' : 'rgba(162,0,255,0.55)'} strokeDasharray={String(circ)} strokeDashoffset={String(offset)} strokeLinecap="round" transform="rotate(-90 13 13)" style={{ transition: 'stroke-dashoffset 0.9s linear, stroke 0.3s' }}/>
        </svg>
        <FiRefreshCw size={11} className={isRefreshing ? 'rc-spin' : ''}/>
      </button>
      <span className="rc-refresh-label">
        {isRefreshing ? 'Refreshing…' : `${secsSince}s ago`}
      </span>
    </div>);
};
const NOTIF_ICON: Record<NotifType, React.ReactNode> = {
    renewal_received: <FiCheckCircle size={13}/>,
    risk_change: <FiAlertTriangle size={13}/>,
    command_completed: <FiCpu size={13}/>,
    price_update: <FiTrendingUp size={13}/>,
    automation_triggered: <FiZap size={13}/>,
    expiring_today: <FiCalendar size={13}/>,
};
const NOTIF_LABEL: Record<NotifType, string> = {
    renewal_received: 'Renewal Received',
    risk_change: 'Risk Change',
    command_completed: 'Command Done',
    price_update: 'Price Update',
    automation_triggered: 'Automation',
    expiring_today: 'Expiring Today',
};
function fmtAge(ts: Date): string {
    const secs = Math.floor((Date.now() - ts.getTime()) / 1000);
    if (secs < 60)
        return 'just now';
    if (secs < 3600)
        return `${Math.floor(secs / 60)}m ago`;
    return `${Math.floor(secs / 3600)}h ago`;
}
const NotificationToast: React.FC<{
    notif: Notification;
    onClose: () => void;
}> = ({ notif, onClose }) => {
    useEffect(() => {
        const t = setTimeout(onClose, 4500);
        return () => clearTimeout(t);
    }, [onClose]);
    return (<motion.div className={`rc-toast${notif.urgent ? ' rc-toast--urgent' : ''}`} initial={{ opacity: 0, y: 32, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.96 }} transition={{ duration: 0.28, ease: [0.34, 1.2, 0.64, 1] }}>
      <span className="rc-toast__icon">{NOTIF_ICON[notif.type]}</span>
      <div className="rc-toast__body">
        <span className="rc-toast__title">{NOTIF_LABEL[notif.type]}</span>
        <span className="rc-toast__msg">{notif.message}</span>
      </div>
      <button className="rc-toast__close" onClick={onClose}><FiX size={12}/></button>
    </motion.div>);
};
const NotificationPanel: React.FC<{
    notifications: Notification[];
    onClose: () => void;
    onMarkAllRead: () => void;
}> = ({ notifications, onClose, onMarkAllRead }) => (<>
    <motion.div className="rc-notif-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} onClick={onClose}/>
    <motion.div className="rc-notif-panel" initial={{ x: 360, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 360, opacity: 0 }} transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}>
      <div className="rc-notif-panel__header">
        <span>Live Notifications</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {notifications.some(n => !n.read) && (<button className="rc-notif-panel__markall" onClick={onMarkAllRead}>Mark all read</button>)}
          <button className="rc-modal__close glass-btn" onClick={onClose}><FiX size={14}/></button>
        </div>
      </div>
      {notifications.length === 0 ? (<div className="rc-notif-empty"><FiBell size={24}/><span>No notifications yet</span></div>) : (<div style={{ overflowY: 'auto', flex: 1 }}>
          {[...notifications].reverse().map(n => (<div key={n.id} className={`rc-notif-item${!n.read ? ' rc-notif-item--unread' : ''}${n.urgent ? ' rc-notif-item--urgent' : ''}`}>
              <span className="rc-notif-item__icon">{NOTIF_ICON[n.type]}</span>
              <div className="rc-notif-item__body">
                <span className="rc-notif-item__insured">{n.insured}</span>
                <span className="rc-notif-item__msg">{n.message}</span>
                <span className="rc-notif-item__time">{NOTIF_LABEL[n.type]} · {fmtAge(n.timestamp)}</span>
              </div>
            </div>))}
        </div>)}
    </motion.div>
  </>);
const StatCard: React.FC<{
    label: string;
    value: string;
    sub?: string;
    icon: React.ReactNode;
    accentClass: string;
    delay?: number;
}> = ({ label, value, sub, icon, accentClass, delay = 0 }) => (<motion.div className={`rc-stat glass-card ${accentClass}`} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: delay * 0.08, ease: 'easeOut' }}>
    <div className="rc-stat__icon">{icon}</div>
    <div className="rc-stat__body">
      <span className="rc-stat__value">{value}</span>
      <span className="rc-stat__label">{label}</span>
      {sub && <span className="rc-stat__sub">{sub}</span>}
    </div>
  </motion.div>);
const COMMAND_TYPES: {
    id: CommandType;
    label: string;
    icon: React.ReactNode;
    plan: string[];
}[] = [
    {
        id: 'shop_quotes', label: 'Shop Quotes', icon: <FiBarChart2 size={14}/>,
        plan: ['Pull market quotes from 3+ carriers via Rate/Win', 'Compare coverage limits and pricing side-by-side', 'Flag best match by retention score and fit', 'Summarize comparison for producer review'],
    },
    {
        id: 'draft_outreach', label: 'Draft Outreach', icon: <FiMail size={14}/>,
        plan: ['Analyze policy status and AI risk level', 'Draft personalized email for insured', 'Queue 3-day follow-up drip in Automation Center', 'Log outreach activity in CRM opportunity'],
    },
    {
        id: 'schedule_followup', label: 'Schedule Follow-up', icon: <FiCalendar size={14}/>,
        plan: ['Check producer calendar availability', 'Create follow-up task with priority flag', 'Draft reminder with full policy context', 'Set CRM activity reminder and deadline'],
    },
    {
        id: 'bind_policy', label: 'Bind Policy', icon: <FiShield size={14}/>,
        plan: ['Verify all renewal documents are complete in AMS', 'Confirm premium and coverage terms with insured', 'Submit bind request to carrier or MGA', 'Update AMS with bound policy confirmation'],
    },
    {
        id: 'request_audit', label: 'Request Audit', icon: <FiFileText size={14}/>,
        plan: ['Pull current audit requirements from carrier', 'Draft audit data request to insured', 'Track submission deadline in calendar', 'Flag overdue items for producer escalation'],
    },
    {
        id: 'custom', label: 'Custom Task', icon: <FiTerminal size={14}/>,
        plan: ['AI analyzes your instruction and policy context', 'Determines required steps and data access', 'Executes with confirmation checkpoints', 'Reports completion status back to you'],
    },
];
const CommandCreateModal: React.FC<{
    row: RenewalRow;
    onClose: () => void;
    onCreate: (cmd: PolicyCommand) => void;
}> = ({ row, onClose, onCreate }) => {
    const [type, setType] = useState<CommandType | null>(null);
    const [priority, setPriority] = useState<CommandPriority>('medium');
    const [description, setDescription] = useState('');
    const selectedType = COMMAND_TYPES.find(t => t.id === type);
    const handleCreate = () => {
        if (!type)
            return;
        onCreate({ id: `cmd-${Date.now()}`, title: type === 'custom' ? description : (selectedType?.label ?? type), status: 'active' });
        onClose();
    };
    return (<SafeAnimatePresence>
      <motion.div className="rc-modal-backdrop" onClick={onClose} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        <motion.div className="rc-cmd-modal glass-card" initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} transition={{ duration: 0.25 }} onClick={e => e.stopPropagation()}>
          <div className="rc-cmd-modal__header">
            <div className="rc-cmd-modal__title"><FiTerminal size={16}/><span>Create Command</span></div>
            <div className="rc-cmd-modal__policy"><span>{row.insured}</span><span className="rc-policy-num">{row.number}</span></div>
            <button className="rc-modal__close glass-btn" onClick={onClose}><FiX size={16}/></button>
          </div>
          <div className="rc-cmd-modal__body">
            <div>
              <div className="rc-cmd-modal__section-label">Task Type</div>
              <div className="rc-cmd-types">
                {COMMAND_TYPES.map(t => (<button key={t.id} className={`rc-cmd-type ${type === t.id ? 'rc-cmd-type--active' : ''}`} onClick={() => setType(t.id)}>
                    {t.icon}<span>{t.label}</span>
                  </button>))}
              </div>
            </div>
            <div>
              <div className="rc-cmd-modal__section-label">Priority</div>
              <div className="rc-priority-btns">
                {(['high', 'medium', 'low'] as CommandPriority[]).map(p => (<button key={p} className={`rc-priority-btn rc-priority-btn--${p} ${priority === p ? 'rc-priority-btn--active' : ''}`} onClick={() => setPriority(p)}>
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>))}
              </div>
            </div>
            {type === 'custom' && (<div>
                <div className="rc-cmd-modal__section-label">Instruction</div>
                <textarea className="rc-cmd-textarea" placeholder="Describe what you want the AI to do for this policy…" value={description} onChange={e => setDescription(e.target.value)} rows={3}/>
              </div>)}
            {selectedType && (<div className="rc-ai-plan">
                <div className="rc-ai-plan__header"><FiCpu size={13}/><span>AI Execution Plan</span></div>
                <ol className="rc-ai-plan__steps">
                  {selectedType.plan.map((step, i) => (<li key={i}><span className="rc-ai-plan__num">{i + 1}</span><span>{step}</span></li>))}
                </ol>
              </div>)}
          </div>
          <div className="rc-cmd-modal__footer">
            <button className="glass-btn" onClick={onClose}>Cancel</button>
            <button className="glass-btn glass-btn-primary" onClick={handleCreate} disabled={!type || (type === 'custom' && !description.trim())}>
              <FiZap size={13}/> Create Command
            </button>
          </div>
        </motion.div>
      </motion.div>
    </SafeAnimatePresence>);
};
const CommandViewModal: React.FC<{
    row: RenewalRow;
    commands: PolicyCommand[];
    onClose: () => void;
    onAddCommand: () => void;
}> = ({ row, commands, onClose, onAddCommand }) => (<SafeAnimatePresence>
    <motion.div className="rc-modal-backdrop" onClick={onClose} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <motion.div className="rc-cmd-modal glass-card" initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} transition={{ duration: 0.25 }} onClick={e => e.stopPropagation()}>
        <div className="rc-cmd-modal__header">
          <div className="rc-cmd-modal__title"><FiTerminal size={16}/><span>Command Center</span></div>
          <div className="rc-cmd-modal__policy"><span>{row.insured}</span><span className="rc-policy-num">{row.number}</span></div>
          <button className="rc-modal__close glass-btn" onClick={onClose}><FiX size={16}/></button>
        </div>
        <div className="rc-cmd-modal__body">
          {commands.map(cmd => (<div key={cmd.id} className="rc-cmd-item">
              <div className={`rc-cmd-item__status rc-cmd-status--${cmd.status}`}>
                {cmd.status === 'completed' ? <FiCheckSquare size={13}/> : cmd.status === 'active' ? <FiPlay size={13}/> : <FiClock size={13}/>}
              </div>
              <div className="rc-cmd-item__content">
                <span className="rc-cmd-item__title">{cmd.title}</span>
                <span className="rc-cmd-item__status-label">{cmd.status.charAt(0).toUpperCase() + cmd.status.slice(1)}</span>
              </div>
            </div>))}
        </div>
        <div className="rc-cmd-modal__footer">
          <button className="glass-btn" onClick={onClose}>Close</button>
          <button className="glass-btn glass-btn-primary" onClick={() => { onClose(); onAddCommand(); }}>
            <FiPlusCircle size={13}/> Add Command
          </button>
        </div>
      </motion.div>
    </motion.div>
  </SafeAnimatePresence>);
const CHAT_CHIPS = ['Prioritize my renewals', 'Shop market candidates', 'Policies expiring this week', 'Show high-risk', 'Give me an overview'];
const INITIAL_MESSAGES: ChatMsg[] = [
    { id: 'welcome', role: 'assistant', content: `I'm monitoring **9 policies** in your renewal book.\n\n**3 high-risk** need immediate attention, **2 are shop-market candidates**, and **4 have no renewal received**.\n\nAsk me anything, or click any policy row to see my full analysis.`, time: now() },
];
const ChatBubble: React.FC<{
    msg: ChatMsg;
}> = ({ msg }) => (<motion.div className={`rc-chat__msg rc-chat__msg--${msg.role}`} initial={{ opacity: 0, y: 8, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.25, ease: 'easeOut' }}>
    {msg.role === 'assistant' && <div className="rc-chat__msg-avatar"><FiCpu size={12}/></div>}
    <div className="rc-chat__msg-body">
      <div className="rc-chat__msg-text">{renderChatContent(msg.content)}</div>
      <span className="rc-chat__msg-time">{msg.time}</span>
    </div>
  </motion.div>);
const TypingIndicator: React.FC = () => (<motion.div className="rc-chat__msg rc-chat__msg--assistant rc-chat__msg--typing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
    <div className="rc-chat__msg-avatar"><FiCpu size={12}/></div>
    <div className="rc-chat__typing-dots"><span /><span /><span /></div>
  </motion.div>);
const RenewalChatPanel: React.FC = () => {
    const [messages, setMessages] = useState<ChatMsg[]>(INITIAL_MESSAGES);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(true);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (expanded)
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, expanded]);
    const handleSend = useCallback((text: string) => {
        const trimmed = text.trim();
        if (!trimmed || loading)
            return;
        setInput('');
        setMessages(prev => [...prev, { id: `u-${Date.now()}`, role: 'user', content: trimmed, time: now() }]);
        setLoading(true);
        setExpanded(true);
        setTimeout(() => {
            setMessages(prev => [...prev, { id: `a-${Date.now()}`, role: 'assistant', content: generateRenewalResponse(trimmed), time: now() }]);
            setLoading(false);
        }, 850 + Math.random() * 400);
    }, [loading]);
    return (<div className="rc-chat-panel glass-card">
      <div className="rc-chat__header" onClick={() => setExpanded(p => !p)}>
        <div className="rc-chat__header-left">
          <div className="rc-chat__pulse"/>
          <FiCpu size={14} className="rc-chat__cpu-icon"/>
          <span className="rc-chat__title">Renewal AI</span>
          <span className="rc-chat__subtitle">monitoring {MOCK_RENEWALS.length} policies</span>
        </div>
        <div className="rc-chat__header-right">
          <span className="rc-chat__msg-count">{messages.length - 1} messages</span>
          <button className="rc-chat__toggle" onClick={e => { e.stopPropagation(); setExpanded(p => !p); }}>
            {expanded ? <FiChevronDown size={14}/> : <FiChevronUp size={14}/>}
          </button>
        </div>
      </div>
      <SafeAnimatePresence>
        {expanded && (<motion.div className="rc-chat__messages-wrap" initial={{ height: 0, opacity: 0 }} animate={{ height: 220, opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: 'easeInOut' }}>
            <div className="rc-chat__messages">
              {messages.map(m => <ChatBubble key={m.id} msg={m}/>)}
              <SafeAnimatePresence>{loading && <TypingIndicator key="typing"/>}</SafeAnimatePresence>
              <div ref={messagesEndRef}/>
            </div>
          </motion.div>)}
      </SafeAnimatePresence>
      <div className="rc-chat__footer">
        <div className="rc-chat__chips">
          {CHAT_CHIPS.map(c => <button key={c} className="rc-chat__chip" onClick={() => handleSend(c)}>{c}</button>)}
        </div>
        <div className="rc-chat__input-row">
          <FiMessageSquare size={14} className="rc-chat__input-icon"/>
          <input className="rc-chat__input" placeholder="Ask about your renewals…" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSend(input)} onFocus={() => setExpanded(true)}/>
          <button className={`glass-btn glass-btn-primary rc-chat__send ${loading ? 'rc-chat__send--loading' : ''}`} onClick={() => handleSend(input)} disabled={loading || !input.trim()}>
            {loading ? <FiRefreshCw size={13} className="rc-spin"/> : <FiSend size={13}/>}
          </button>
        </div>
      </div>
    </div>);
};
const RETENTION_LOST_CFG: Record<RetentionLostReason, {
    label: string;
    color: string;
}> = {
    rewritten_away: { label: 'Rewritten Away', color: 'var(--accent-orange)' },
    carrier_non_renewed: { label: 'Carrier Non-Renewed', color: 'var(--accent-yellow)' },
    client_cancelled: { label: 'Client Left', color: 'var(--text-secondary)' },
    rate_increase: { label: 'Rate Increase', color: 'var(--accent-red)' },
    unknown: { label: 'Unknown', color: 'var(--text-muted)' },
};
const RETENTION_VERDICT: Record<string, string> = {
    rewritten_away_true: 'Rewrite Detected',
    carrier_non_renewed_true: 'Avoidable Loss',
    rate_increase_true: 'Rate Churn',
    client_cancelled_true: 'Contested Loss',
    rewritten_away_false: 'Expected Exit',
    carrier_non_renewed_false: 'Carrier Non-Ren',
    client_cancelled_false: 'Client Left',
    rate_increase_false: 'Priced Out',
    unknown_true: 'Investigate',
    unknown_false: 'Expected Loss',
};
const OUTCOME_LABEL: Record<string, string> = {
    cancelled: 'CANCELLED',
    expired: 'EXPIRED',
    non_renewed: 'NON-RENEWED',
};
const RetentionExpandPanel: React.FC<{
    row: RetentionRow;
    markedStatus?: 'renewed' | 'replaced';
    onMark: (s: 'renewed' | 'replaced') => void;
}> = ({ row, markedStatus, onMark }) => {
    const ev = row.evidence.sameLoBDifferentCarrier;
    const hasReplacement = !!ev;
    const gapDays = ev ? daysBetween(row.expiredDate, ev.effectiveDate) : 0;
    const verdictKey = `${row.lostReason}_${row.aiShouldRetain}`;
    const verdictLabel = RETENTION_VERDICT[verdictKey] ?? 'Review';
    return (<motion.div className="rc-reasoning" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.28, ease: [0.34, 1.2, 0.64, 1] }}>
      <div className="rc-reasoning__inner">

        <div className="rc-ret-exp-header">
          <span className={`rc-ret-verdict ${row.aiShouldRetain ? 'rc-ret-verdict--yes' : 'rc-ret-verdict--no'}`}>
            {row.aiShouldRetain ? <FiAlertTriangle size={10}/> : <FiCheckCircle size={10}/>}
            {verdictLabel}
          </span>
          <span className="rc-ret-exp-conf">AI confidence: {row.aiConfidence}%</span>
        </div>

        <div className="rc-ret-comp">
          <motion.div className="rc-ret-comp-card rc-ret-comp-card--lost" initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.06, duration: 0.3 }}>
            <div className="rc-ret-comp-tag">{OUTCOME_LABEL[row.outcome] ?? row.outcome.toUpperCase()}</div>
            <div className="rc-ret-comp-carrier">{row.carrier}</div>
            <div className="rc-ret-comp-lob">{row.lineOfBusiness}</div>
            <div className="rc-ret-comp-premium">{fmt$(row.premium)}<span className="rc-ret-comp-perlabel">/yr</span></div>
            <div className="rc-ret-comp-date">exp. {row.expiredDate.slice(5).replace('-', '/')}</div>
          </motion.div>

          <div className="rc-ret-comp-arrow">
            <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.18, duration: 0.25 }}>
              {hasReplacement ? <FiArrowRight size={18}/> : <FiMinus size={16}/>}
            </motion.div>
            {hasReplacement
            ? <span className="rc-ret-comp-gap">{gapDays}d gap</span>
            : <span className="rc-ret-comp-gap rc-ret-comp-gap--none">No match found</span>}
          </div>

          {hasReplacement ? (<motion.div className="rc-ret-comp-card rc-ret-comp-card--new" initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1, duration: 0.3 }}>
              <div className="rc-ret-comp-tag rc-ret-comp-tag--new">REPLACED WITH</div>
              <div className="rc-ret-comp-carrier rc-ret-comp-carrier--new">{ev!.carrier}</div>
              <div className="rc-ret-comp-lob">{row.lineOfBusiness}</div>
              {ev!.estimatedPremium ? (<div className="rc-ret-comp-premium rc-ret-comp-premium--new">
                  ~{fmt$(ev!.estimatedPremium)}<span className="rc-ret-comp-perlabel">/yr</span>
                  {ev!.estimatedPremium < row.premium && (<span className="rc-ret-comp-savings">−{fmt$(row.premium - ev!.estimatedPremium)}</span>)}
                </div>) : null}
              <div className="rc-ret-comp-date">eff. {ev!.effectiveDate.slice(5).replace('-', '/')}</div>
              <div className="rc-ret-comp-polnum">{ev!.policyNumber}</div>
            </motion.div>) : (<motion.div className="rc-ret-comp-card rc-ret-comp-card--empty" initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1, duration: 0.3 }}>
              <div className="rc-ret-comp-tag rc-ret-comp-tag--empty">NO REPLACEMENT</div>
              <div className="rc-ret-comp-empty-msg">No competing policy detected after expiration.</div>
            </motion.div>)}
        </div>

        <div className="rc-ret-exp-analysis">
          <FiCpu size={10}/>
          <p>{row.aiAnalysis}</p>
        </div>

        {!markedStatus ? (<div className="rc-ret-actions">
            <span className="rc-ret-actions__label">Mark outcome:</span>
            <button className="rc-ret-action-btn rc-ret-action-btn--renewed" onClick={() => onMark('renewed')}>
              <FiCheckCircle size={11}/>Mark Renewed
            </button>
            <button className="rc-ret-action-btn rc-ret-action-btn--replaced" onClick={() => onMark('replaced')}>
              <FiArrowRight size={11}/>Mark Replaced
            </button>
          </div>) : (<motion.div className="rc-ret-marked" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
            <FiCheckCircle size={14}/>
            <span>Marked as {markedStatus === 'renewed' ? 'Renewed' : 'Replaced by another policy'}</span>
          </motion.div>)}
      </div>
    </motion.div>);
};
const RetentionTable: React.FC = () => {
    const [expandedId, setExpandedId] = React.useState<string | null>(null);
    const [markedPolicies, setMarkedPolicies] = React.useState<Record<string, 'renewed' | 'replaced'>>({});
    const toggle = (id: string) => setExpandedId(p => p === id ? null : id);
    const markPolicy = (id: string, status: 'renewed' | 'replaced') => setMarkedPolicies(p => ({ ...p, [id]: status }));
    return (<div className="rc-table">
      <div className="rc-ret-header">
        <span>Expired</span><span>Number</span><span>Insured</span><span>LOB</span>
        <span>Carrier</span><span>Last Premium</span>
        <span className="rc-col--center">Outcome</span>
        <span className="rc-col--center">AI Verdict</span>
        <span>Lost Reason</span><span></span>
      </div>
      {MOCK_RETENTION.map((row, i) => {
            const isExpanded = expandedId === row.id;
            const daysAgo = Math.floor((Date.now() - new Date(row.expiredDate).getTime()) / 86400000);
            const { label: lostLabel, color: lostColor } = RETENTION_LOST_CFG[row.lostReason];
            const outcomeCls = row.outcome === 'cancelled' ? 'rc-badge--cancelled' : 'rc-badge--archived';
            return (<React.Fragment key={row.id}>
            <motion.div className={`rc-row rc-row--data${isExpanded ? ' rc-row--expanded' : ''}`} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }} onClick={() => toggle(row.id)}>
              <div className="rc-row__cells rc-ret-cells">
                <span className="rc-exp-date">
                  {row.expiredDate.slice(5).replace('-', '/')}
                  <span className="rc-exp-days" style={{ background: 'rgba(255,255,255,0.07)', color: 'var(--text-muted)' }}>{daysAgo}d ago</span>
                </span>
                <span className="rc-policy-num">{row.number}</span>
                <span className="rc-insured">{row.insured}</span>
                <span className="rc-lob">{row.lineOfBusiness}</span>
                <span className="rc-carrier" title={row.carrier}>{row.carrier.length > 22 ? row.carrier.slice(0, 22) + '…' : row.carrier}</span>
                <span className="rc-premium">{fmt$(row.premium)}</span>
                <span className="rc-col--center"><span className={`rc-badge ${outcomeCls}`}>{row.outcome.replace('_', ' ')}</span></span>
                <span className="rc-col--center">
                  {markedPolicies[row.id] ? (<span className="rc-ret-verdict rc-ret-verdict--marked">
                      <FiCheckCircle size={9}/>{markedPolicies[row.id] === 'renewed' ? 'Renewed' : 'Replaced'}
                    </span>) : (<span className={`rc-ret-verdict ${row.aiShouldRetain ? 'rc-ret-verdict--yes' : 'rc-ret-verdict--no'}`}>
                      {row.aiShouldRetain ? <FiAlertTriangle size={9}/> : <FiCheck size={9}/>}
                      <span>{RETENTION_VERDICT[`${row.lostReason}_${row.aiShouldRetain}`] ?? `${row.aiConfidence}%`}</span>
                    </span>)}
                </span>
                <span className="rc-ret-lost" style={{ color: lostColor }}>{lostLabel}</span>
                <span className="rc-row__expand-arrow">
                  <FiChevronRight size={12} className={isExpanded ? 'rc-row__expand-arrow--open' : ''}/>
                </span>
              </div>
            </motion.div>
            <SafeAnimatePresence>
              {isExpanded && <RetentionExpandPanel key={`ret-${row.id}`} row={row} markedStatus={markedPolicies[row.id]} onMark={s => markPolicy(row.id, s)}/>}
            </SafeAnimatePresence>
          </React.Fragment>);
        })}
    </div>);
};
const RenewalsPage: React.FC = () => {
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [lobFilter, setLobFilter] = useState('All');
    const [riskFilter, setRiskFilter] = useState('All');
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [retentionMode, setRetentionMode] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [secsSince, setSecsSince] = useState(0);
    const [compareRow, setCompareRow] = useState<RenewalRow | null>(null);
    const [commandCreateRow, setCommandCreateRow] = useState<RenewalRow | null>(null);
    const [commandViewRow, setCommandViewRow] = useState<{
        row: RenewalRow;
        commands: PolicyCommand[];
    } | null>(null);
    const [newCommands, setNewCommands] = useState<Record<string, PolicyCommand[]>>({});
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [activeToast, setActiveToast] = useState<Notification | null>(null);
    const [notifsOpen, setNotifsOpen] = useState(false);
    const [updatedRows, setUpdatedRows] = useState<Set<string>>(new Set());
    const liveEventIndexRef = useRef(0);
    const triggerRefresh = useCallback(() => {
        setIsRefreshing(true);
        setSecsSince(0);
        setTimeout(() => setIsRefreshing(false), 1200);
    }, []);
    useEffect(() => {
        const tick = setInterval(() => {
            setSecsSince(prev => {
                if (prev >= 29) {
                    triggerRefresh();
                    return 0;
                }
                return prev + 1;
            });
        }, 1000);
        return () => clearInterval(tick);
    }, [triggerRefresh]);
    useEffect(() => {
        let timeoutId: ReturnType<typeof setTimeout>;
        const fireNextEvent = () => {
            const idx = liveEventIndexRef.current;
            if (idx >= LIVE_EVENTS.length)
                return;
            const event = LIVE_EVENTS[idx];
            liveEventIndexRef.current = idx + 1;
            const notif: Notification = {
                id: `notif-${Date.now()}-${idx}`,
                type: event.type,
                policyId: event.policyId,
                insured: event.insured,
                message: event.message,
                timestamp: new Date(),
                read: false,
                urgent: event.urgent,
            };
            setNotifications(prev => [...prev, notif]);
            setActiveToast(notif);
            setUpdatedRows(prev => { const next = new Set(prev); next.add(event.policyId); return next; });
            if (liveEventIndexRef.current < LIVE_EVENTS.length) {
                const delay = 20000 + Math.random() * 15000;
                timeoutId = setTimeout(fireNextEvent, delay);
            }
        };
        timeoutId = setTimeout(fireNextEvent, 8000);
        return () => clearTimeout(timeoutId);
    }, []);
    const toggleExpand = useCallback((id: string) => {
        setExpandedId(p => {
            if (p !== id) {
                setUpdatedRows(prev => {
                    const next = new Set(prev);
                    next.delete(id);
                    return next;
                });
            }
            return p === id ? null : id;
        });
    }, []);
    const addCommand = useCallback((rowId: string, cmd: PolicyCommand) => {
        setNewCommands(prev => ({ ...prev, [rowId]: [...(prev[rowId] || []), cmd] }));
    }, []);
    const accountGroups = useMemo(() => {
        const map: Record<string, RenewalRow[]> = {};
        MOCK_RENEWALS.forEach(r => {
            const key = r.insured.toLowerCase().trim();
            if (!map[key])
                map[key] = [];
            map[key].push(r);
        });
        return map;
    }, []);
    const statuses = ['All', 'Active', 'Renewed', 'Cancelled', 'Archived'];
    const lobs = ['All', ...Array.from(new Set(MOCK_RENEWALS.map(r => r.lineOfBusiness)))];
    const risks = ['All', 'High', 'Medium', 'Low'];
    const filtered = MOCK_RENEWALS.filter(r => {
        const q = search.toLowerCase();
        return ((!q || r.insured.toLowerCase().includes(q) || r.number.toLowerCase().includes(q) || r.carrier.toLowerCase().includes(q))
            && (statusFilter === 'All' || r.status === statusFilter)
            && (lobFilter === 'All' || r.lineOfBusiness === lobFilter)
            && (riskFilter === 'All' || r.aiRisk === riskFilter.toLowerCase()));
    });
    const highRisk = MOCK_RENEWALS.filter(r => r.aiRisk === 'high').length;
    const totalAtRisk = MOCK_RENEWALS.filter(r => r.pctChange > 10).reduce((s, r) => s + r.renewalPremium, 0);
    const withInc = MOCK_RENEWALS.filter(r => r.pctChange > 0);
    const avgPct = withInc.length ? +(withInc.reduce((s, r) => s + r.pctChange, 0) / withInc.length).toFixed(1) : 0;
    const noRenewal = MOCK_RENEWALS.filter(r => !r.renewalPolicy && !r.renewalQuote).length;
    return (<div className="rc-page">
      <div className="rc-dashboard">
        <div className="rc-stats">
          <StatCard label="Policies Expiring" value={String(MOCK_RENEWALS.length)} sub="Next 30 days" icon={<FiCalendar size={18}/>} accentClass="rc-stat--purple" delay={0}/>
          <StatCard label="Premium at Risk" value={`$${(totalAtRisk / 1000).toFixed(1)}k`} sub=">10% increase" icon={<FiDollarSign size={18}/>} accentClass="rc-stat--cyan" delay={1}/>
          <StatCard label="Avg % Increase" value={`+${avgPct}%`} sub="Policies with change" icon={<FiTrendingUp size={18}/>} accentClass="rc-stat--amber" delay={2}/>
          <StatCard label="AI Flagged" value={String(highRisk + noRenewal)} sub={`${highRisk} high-risk · ${noRenewal} no renewal`} icon={<FiZap size={18}/>} accentClass="rc-stat--red" delay={3}/>
        </div>

        <motion.div className="rc-filters glass-card" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <div className="rc-filters__top">
            <div className="rc-filters__search-wrap">
              <FiSearch size={14} className="rc-filters__search-icon"/>
              <input className="rc-filters__search" placeholder="Search insured, policy number, carrier…" value={search} onChange={e => setSearch(e.target.value)}/>
              {search && <button className="rc-filters__clear" onClick={() => setSearch('')}><FiX size={13}/></button>}
            </div>
            <div className="rc-filters__selects">
              <select className="rc-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                {statuses.map(s => <option key={s}>{s}</option>)}
              </select>
              <select className="rc-select" value={lobFilter} onChange={e => setLobFilter(e.target.value)}>
                {lobs.map(l => <option key={l}>{l}</option>)}
              </select>
              <div className="rc-risk-filters">
                {risks.map(r => (<button key={r} className={`rc-risk-btn ${riskFilter === r ? 'rc-risk-btn--active' : ''} ${r !== 'All' ? `rc-risk-btn--${r.toLowerCase()}` : ''}`} onClick={() => setRiskFilter(r)}>
                    {r === 'High' && <FiAlertTriangle size={11}/>}{r === 'Medium' && <FiActivity size={11}/>}{r === 'Low' && <FiCheckCircle size={11}/>}{r === 'All' && <FiFilter size={11}/>}{r}
                  </button>))}
              </div>
            </div>
            <button className="glass-btn rc-filters__toggle" onClick={() => setFiltersOpen(p => !p)}>
              <FiFilter size={14}/>Filters{filtersOpen ? <FiChevronUp size={13}/> : <FiChevronDown size={13}/>}
            </button>
          </div>
          <SafeAnimatePresence>
            {filtersOpen && (<motion.div className="rc-filters__extra" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.25 }}>
                <div className="rc-filters__extra-grid">
                  <div className="rc-filter-group"><label>% Change</label><select className="rc-select"><option>Any</option><option>0%</option><option>1–5%</option><option>6–15%</option><option>&gt;15%</option></select></div>
                  <div className="rc-filter-group"><label>Renewal Status</label><select className="rc-select"><option>All</option><option>Has Renewal Policy</option><option>Has Renewal Quote</option><option>No Renewal</option></select></div>
                  <div className="rc-filter-group"><label>AI Recommendation</label><select className="rc-select"><option>All</option><option>Accept Renewal</option><option>Shop Market</option><option>Contact Insured</option></select></div>
                </div>
              </motion.div>)}
          </SafeAnimatePresence>
        </motion.div>

        <motion.div className="rc-table-wrap glass-card" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.28 }}>
          <div className="rc-table-header">
            <div className="rc-view-toggle">
              <button className={`rc-view-btn${!retentionMode ? ' rc-view-btn--active' : ''}`} onClick={() => setRetentionMode(false)}>
                <FiRefreshCw size={12}/>Renewals
              </button>
              <button className={`rc-view-btn${retentionMode ? ' rc-view-btn--active' : ''}`} onClick={() => setRetentionMode(true)}>
                <FiUsers size={12}/>Retention
              </button>
            </div>
            <div className="rc-table-header__right">
              <span className="rc-table-count">
                <span className="rc-live-dot"/>
                {retentionMode ? MOCK_RETENTION.length : filtered.length} policies
              </span>
              
              <button className="rc-notif-bell" onClick={e => { e.stopPropagation(); setNotifsOpen(p => !p); setNotifications(prev => prev.map(n => ({ ...n, read: true }))); }} title="Notifications">
                <FiBell size={15}/>
                {notifications.filter(n => !n.read).length > 0 && (<span className="rc-notif-badge">{notifications.filter(n => !n.read).length}</span>)}
              </button>
              <RefreshButton isRefreshing={isRefreshing} secsSince={secsSince} onRefresh={triggerRefresh}/>
            </div>
          </div>
          <div className="rc-table">
            {retentionMode ? <RetentionTable /> : (<>
            <div className="rc-row rc-row--head">
              <span>Exp. Date</span><span>Number</span><span>Insured</span><span>Line of Business</span>
              <span>Carrier</span><span>Premium</span><span>Renewal Premium</span>
              <span className="rc-col--center">Policy</span><span className="rc-col--center">Quote</span>
              <span className="rc-col--center">Change</span><span className="rc-col--center">AI Risk</span><span className="rc-col--center">Status</span><span></span>
            </div>

            {filtered.map((row, i) => {
                const days = daysUntil(row.expDate);
                const isExpanded = expandedId === row.id;
                const allCmds = [...row.commands, ...(newCommands[row.id] || [])];
                const acctKey = row.insured.toLowerCase().trim();
                const acctRows = accountGroups[acctKey] || [];
                const acctTotal = acctRows.reduce((s, r) => s + (r.renewalPremium > 0 ? r.renewalPremium : r.premium), 0);
                return (<React.Fragment key={row.id}>
                  <motion.div className={`rc-row rc-row--data ${isExpanded ? 'rc-row--expanded' : ''}${days <= 0 ? ' rc-row--urgent' : days <= 7 ? ' rc-row--soon' : ''}${updatedRows.has(row.id) ? ' rc-row--updated' : ''}`} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }} onClick={() => toggleExpand(row.id)}>
                    
                    <div className="rc-row__cells">
                      <span className={`rc-exp-date ${days <= 0 ? 'rc-exp-date--urgent' : days <= 7 ? 'rc-exp-date--soon' : days <= 30 ? 'rc-exp-date--upcoming' : ''}`}>
                        {days <= 0 ? <FiAlertTriangle size={11}/> : <FiCalendar size={11}/>}
                        {row.expDate.slice(5).replace('-', '/')}
                        {days <= 0 && <span className="rc-exp-today">Today</span>}
                        {days > 0 && days <= 7 && <span className="rc-exp-days">{days}d</span>}
                      </span>
                      <span className="rc-policy-num">
                        {row.number}
                        {acctRows.length > 1 && (<span className="rc-policy-acct-badge" title={`${acctRows.length} policies renewing · total ${fmt$(acctTotal)}`}>
                            <FiFileText size={8}/>{acctRows.length}
                          </span>)}
                      </span>
                      <span className="rc-insured">{row.insured}</span>
                      <span className="rc-lob">{row.lineOfBusiness}</span>
                      <span className="rc-carrier" title={row.carrier}>{row.carrier.length > 22 ? row.carrier.slice(0, 22) + '…' : row.carrier}</span>
                      <span className="rc-premium">{fmt$(row.premium)}</span>
                      <span className={`rc-renewal-premium ${row.renewalPremium > row.premium ? 'rc-renewal-premium--up' : ''}`}>
                        {row.renewalPremium === 0 ? <span className="rc-no-data">—</span> : fmt$(row.renewalPremium)}
                      </span>
                      <span className="rc-col--center">
                        {row.renewalPolicy ? <span className="rc-has-doc" title={row.renewalPolicy}><FiCheckCircle size={13}/></span> : <span className="rc-no-doc"><FiMinus size={11}/></span>}
                      </span>
                      <span className="rc-col--center">
                        {row.renewalQuote ? <span className="rc-has-doc rc-has-doc--quote" title={row.renewalQuote}><FiFileText size={13}/></span> : <span className="rc-no-doc"><FiMinus size={11}/></span>}
                      </span>
                      <span className="rc-col--center" onClick={e => e.stopPropagation()}>
                        <ChangeBadge pct={row.pctChange} dollar={row.dollarChange} onClick={() => toggleExpand(row.id)} expanded={isExpanded}/>
                      </span>
                      <span className="rc-col--center"><RiskBadge risk={row.aiRisk}/></span>
                      <span className="rc-col--center"><StatusBadge status={row.status}/></span>
                      <span className="rc-row__expand-arrow">
                        <FiChevronRight size={12} className={isExpanded ? 'rc-row__expand-arrow--open' : ''}/>
                      </span>
                    </div>

                    
                    <WorkflowBar row={row} allCommands={allCmds} accountCount={acctRows.length} accountTotal={acctTotal} accountRows={acctRows} onCreateCommand={e => { e.stopPropagation(); setCommandCreateRow(row); }}/>
                  </motion.div>

                  
                  <SafeAnimatePresence>
                    {isExpanded && (<AIReasoningPanel key={`reason-${row.id}`} row={row} allCommands={allCmds} onViewSummary={() => { if (row.currentSnapshot && row.renewalSnapshot)
                        setCompareRow(row); }} onCreateCommand={() => setCommandCreateRow(row)} onViewCommands={() => setCommandViewRow({ row, commands: allCmds })}/>)}
                  </SafeAnimatePresence>
                </React.Fragment>);
            })}

            {filtered.length === 0 && (<div className="rc-empty"><FiSearch size={24}/><span>No renewals match your filters</span></div>)}
            </>)}
          </div>
        </motion.div>
      </div>

      <RenewalChatPanel />

      {compareRow && compareRow.currentSnapshot && compareRow.renewalSnapshot && (<PolicyComparisonModal row={compareRow} onClose={() => setCompareRow(null)}/>)}
      {commandCreateRow && (<CommandCreateModal row={commandCreateRow} onClose={() => setCommandCreateRow(null)} onCreate={cmd => addCommand(commandCreateRow.id, cmd)}/>)}
      {commandViewRow && (<CommandViewModal row={commandViewRow.row} commands={commandViewRow.commands} onClose={() => setCommandViewRow(null)} onAddCommand={() => setCommandCreateRow(commandViewRow.row)}/>)}

      
      <SafeAnimatePresence>
        {notifsOpen && (<NotificationPanel key="notif-panel" notifications={notifications} onClose={() => setNotifsOpen(false)} onMarkAllRead={() => setNotifications(prev => prev.map(n => ({ ...n, read: true })))}/>)}
      </SafeAnimatePresence>

      
      <SafeAnimatePresence>
        {activeToast && (<NotificationToast key={activeToast.id} notif={activeToast} onClose={() => setActiveToast(null)}/>)}
      </SafeAnimatePresence>
    </div>);
};
export default RenewalsPage;
